import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import locationService from '../../../api/services/locationService';
import { API_BASE_URL } from '../../../api/config';
import {
  FileText,
  Download,
  Eye,
  Send,
  Plus,
  Search,
  RefreshCw,
  User,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  CheckCircle2,
  X,
  Trash2,
  Edit,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
} from 'lucide-react';
import { formatDate } from '../../../utils/stringUtils';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { usePermissions } from '../../../hooks/usePermissions';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import Button from '../../../components/common/Button/Button';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import useUrlTableState from '../../../hooks/useUrlTableState';

const RelievingLetter = ({ onBack, userRole = 'hr_manager' }) => {
  const navigate = useNavigate();
  const { hasPermission, canCreate, canUpdate, canDelete } = usePermissions();
  // const [activeTab, setActiveTab] = useState('dashboard');
  const [showLetterForm, setShowLetterForm] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [relievingLetters, setRelievingLetters] = useState([]);

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterStatus: 'all',
    filterDepartment: 'all',
    filterStartDate: '',
    filterEndDate: ''
  }), []);

  const {
    filters: letterFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateLetterFilter,
    updatePage: updateLetterPage,
    updateLimit: updateLetterLimit,
    resetFilters: resetLetterFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;
  const debouncedSearchTerm = searchTerm; // Hook handles debouncing internally

  const statusFilter = letterFilters.filterStatus || 'all';
  const setStatusFilter = useCallback((status) => {
    updateLetterFilter({ filterStatus: status });
  }, [updateLetterFilter]);

  const departmentFilter = letterFilters.filterDepartment || 'all';
  const setDepartmentFilter = useCallback((department) => {
    updateLetterFilter({ filterDepartment: department });
  }, [updateLetterFilter]);

  const startDate = letterFilters.filterStartDate || '';
  const endDate = letterFilters.filterEndDate || '';

  const setDateRange = useCallback((range) => {
    updateLetterFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updateLetterFilter]);

  const setStartDate = useCallback((date) => {
    updateLetterFilter({ filterStartDate: date });
  }, [updateLetterFilter]);

  const setEndDate = useCallback((date) => {
    updateLetterFilter({ filterEndDate: date });
  }, [updateLetterFilter]);

  const lettersPage = letterFilters.page;
  const lettersPageSize = letterFilters.limit;

  const setLettersPage = useCallback((page) => {
    updateLetterPage(page);
  }, [updateLetterPage]);

  const setLettersPageSize = useCallback((limit) => {
    updateLetterLimit(limit);
  }, [updateLetterLimit]);

  const [lettersTotalPages, setLettersTotalPages] = useState(1);
  const [lettersTotalItems, setLettersTotalItems] = useState(0);
  const [letterStats, setLetterStats] = useState({});
  const [departments, setDepartments] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLetter, setPreviewLetter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [letterTouchedFields, setLetterTouchedFields] = useState(new Set());

  const handleLetterFieldBlur = (field) => {
    setLetterTouchedFields((prev) => {
      const newSet = new Set(prev);
      newSet.add(field);
      return newSet;
    });
  };

  // Load departments logic (consistent with NoDues.jsx and ExitRequests.jsx)
  const loadDepartments = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/hr/departments?page=1&limit=10&sortBy=createdAt&sortOrder=desc&isActive=all&paginated=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        mode: 'cors'
      });

      if (!response.ok) {
        if (response.status === 401) {
        }
        throw new Error('Failed to load departments');
      }

      const result = await response.json();
      const raw = result?.data?.docs || result?.data?.departments || result?.data || result?.departments || result || [];
      const names = (Array.isArray(raw) ? raw : [])
        .map((d) => (typeof d === 'string' ? d : d?.name || d?.departmentName || d?.label || d?.value))
        .filter(Boolean);

      if (names.length) {
        setDepartments((prev) => Array.from(new Set([...(prev || []), ...names])));
      }
    } catch (err) {
    }
  };

  useEffect(() => {
    loadDepartments();
  }, []);

  const [employees, setEmployees] = useState([]);
  const [exitRequests, setExitRequests] = useState([]);
  const [noDuesClearances, setNoDuesClearances] = useState([]);
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState('');
  const [backendStatus, setBackendStatus] = useState('checking'); // 'checking', 'connected', 'disconnected'
  const [noDuesSearchTerm, setNoDuesSearchTerm] = useState('');
  const [noDuesOpen, setNoDuesOpen] = useState(false);
  const [showNoDuesDropdown, setShowNoDuesDropdown] = useState(false);
  const noDuesDropdownRef = useRef(null);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState('');
  const employeeDropdownRef = useRef(null);
  const [reportingManagerSearchTerm, setReportingManagerSearchTerm] = useState('');
  const [isReportingManagerDropdownOpen, setIsReportingManagerDropdownOpen] = useState(false);
  const [debouncedReportingManagerSearch, setDebouncedReportingManagerSearch] = useState('');
  const reportingManagerDropdownRef = useRef(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [letterToSend, setLetterToSend] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [letterToDelete, setLetterToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [signatorySearchTerm, setSignatorySearchTerm] = useState('');
  const [isSignatoryDropdownOpen, setIsSignatoryDropdownOpen] = useState(false);
  const [debouncedSignatorySearch, setDebouncedSignatorySearch] = useState('');
  const signatoryDropdownRef = useRef(null);
  const [selectedSignatory, setSelectedSignatory] = useState(null);
  const queryClient = useQueryClient();

  const resolveEmployeeName = (emp) => {
    if (!emp) return '';
    const name = emp.fullName ||
      emp.name ||
      emp.employeeName ||
      (emp.personalInfo?.firstName ? `${emp.personalInfo.firstName} ${emp.personalInfo.lastName || ''}`.trim() : null) ||
      (emp.firstName ? `${emp.firstName} ${emp.lastName || ''}`.trim() : null) ||
      'Unknown Name';
    return name;
  };

  // Permission-based access control
  const canCreateLetter = hasPermission('exit_management', 'create');
  const canViewAllLetters = hasPermission('exit_management', 'read');
  const canDeleteLetter = hasPermission('exit_management', 'delete');
  const canIssueLetter = hasPermission('exit_management', 'update');
  const canGenerateSampleData = hasPermission('exit_management', 'create'); // Sample data generation requires create permission

  // Export State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Toggle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(relievingLetters.map(l => l._id));
      setSelectedRows(allIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  // Toggle select row
  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Handle general export button click
  const handleExport = () => {
    setShowExportModal(true);
  };

  // Map data for export
  const mapLetterForExport = (data) => {
    return data.map(l => ({
      'Letter ID': l.letterId || '',
      'Employee Name': l.employeeName || '',
      'Email': l.employeeEmail || '',
      'Department': l.department || '',
      'Position': l.position || '',
      'Status': l.status || '',
      'Created Date': l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN') : '',
      'Approved By': l.approvalDetails?.approvedByName || '',
      'Approved Date': l.approvalDetails?.approvedDate ? new Date(l.approvalDetails.approvedDate).toLocaleDateString('en-IN') : '',
      'Issued Date': l.deliveryDetails?.deliveryDate ? new Date(l.deliveryDetails.deliveryDate).toLocaleDateString('en-IN') : ''
    }));
  };

  // Export Selected Rows (Local)
  const handleExportSelected = () => {
    const selectedData = relievingLetters.filter(l => selectedRows.has(l._id));
    if (selectedData.length === 0) {
      alert("No rows selected for export.");
      return;
    }
    const csvData = mapLetterForExport(selectedData);
    exportToCSV(csvData, 'relieving_letters_selected', { title: 'Relieving Letter Report', type: 'Selected Rows' });
    setSelectedRows(new Set());
  };

  // Export Current Page (Local)
  const handleExportCurrentPage = () => {
    if (relievingLetters.length === 0) {
      alert("No data available on the current page to export.");
      return;
    }
    const csvData = mapLetterForExport(relievingLetters);
    exportToCSV(csvData, 'relieving_letters_page', { title: 'Relieving Letter Report', type: 'Current Page Data' });
    setShowExportModal(false);
  };

  // Export Date Range (Backend)
  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('startDate', startDate);
      queryParams.append('endDate', endDate);
      queryParams.append('type', `Date Range (${startDate} to ${endDate})`);

      if (statusFilter !== 'all') queryParams.append('status', statusFilter);
      if (departmentFilter !== 'all') queryParams.append('department', departmentFilter);
      if (searchTerm) queryParams.append('search', searchTerm);

      await downloadFromBackend(
        `${API_BASE_URL}/relieving-letter/export?${queryParams.toString()}`,
        generateExportFilename('relieving_letters_range'),
        { 'x-user-role': userRole }
      );
      setShowExportModal(false);
    } catch (error) {
      alert("Failed to export data. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  // Export All Data (Backend)
  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('type', 'All Data');

      if (statusFilter !== 'all') queryParams.append('status', statusFilter);
      if (departmentFilter !== 'all') queryParams.append('department', departmentFilter);
      if (searchTerm) queryParams.append('search', searchTerm);

      await downloadFromBackend(
        `${API_BASE_URL}/relieving-letter/export?${queryParams.toString()}`,
        generateExportFilename('relieving_letters_all'),
        { 'x-user-role': userRole }
      );
      setShowExportModal(false);
    } catch (error) {
      alert("Failed to export data. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  // Form state
  const [editingLetterId, setEditingLetterId] = useState(null);
  const [letterForm, setLetterForm] = useState({
    employeeId: '',
    exitRequestId: '',
    noDuesClearanceId: '',
    resignationDate: '',
    acceptanceDate: '',
    noticePeriodDays: 0,
    noticeServedDays: 0,
    noticeShortfallDays: 0,
    noticeBuyoutAmount: 0,
    exitType: 'voluntary',
    separationCategory: '',
    noDuesStatus: 'pending',
    fnfStatus: 'pending',
    letterNumber: '',
    versionNumber: 1,
    issueDate: '',
    status: 'draft',
    authorization: {
      signatoryId: '',
      name: '',
      email: '',
      role: '',
      department: '',
      digitalSignatureUrl: '',
      companySealUrl: ''
    },
    employmentDetails: {
      joinDate: '',
      lastWorkingDate: '',
      employmentType: 'full-time',
      workLocation: '',
      reportingManager: {
        employeeId: '',
        name: '',
        email: '',
        role: '',
        department: ''
      },
      reasonForLeaving: 'resignation',
      reasonDescription: ''
    },
    performanceDetails: {
      overallRating: 'good',
      keyAchievements: [],
      skills: '',
      performanceNotes: ''
    },
    financialDetails: {
      lastSalary: 0,
      currency: 'INR',
      benefits: [],
    },
    letterContent: {
      subject: '',
      salutation: 'To Whom It May Concern,',
      body: '',
      closing: 'Yours sincerely,',
      signature: '',
      designation: '',
      companyName: '',
      companyAddress: ''
    },
    priority: 'medium'
  });

  const selectedEmployeeLabel = useMemo(() => {
    const selected = employees.find((e) => {
      const formEmpId = String(letterForm.employeeId || '');
      return String(e._id || '') === formEmpId ||
        String(e.id || '') === formEmpId ||
        String(e.employeeId || '') === formEmpId;
    });
    if (!selected) {
      return 'Select Employee';
    }
    const name =
      selected.fullName ||
      `${selected.firstName || ''} ${selected.lastName || ''}`.trim() ||
      selected.employeeName ||
      '';
    const dept = selected.department || selected.employmentInfo?.department || '';
    return `${name || 'Select Employee'}${dept ? ` - ${dept}` : ''}`;
  }, [employees, letterForm.employeeId]);

  // Helper function for text input key press - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  // Helper function for number input key down (positive numbers only, 0 and greater)
  const handleNumberKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow numbers on top of keyboard
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      // Allow numbers on numpad
      (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Block: minus, plus, e, E, period (for decimal)
    if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
      e.preventDefault();
    }
  };

  // Helper function for search input key press - allow approved symbols
  const handleSearchKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  // React Query: Load relieving letters (paginated)
  const { data: lettersData, isLoading: isLoadingLetters } = useQuery({
    queryKey: ['relievingLetters', lettersPage, lettersPageSize, statusFilter, departmentFilter, searchTerm, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(lettersPage));
      params.set('limit', String(lettersPageSize));
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (departmentFilter && departmentFilter !== 'all') params.set('department', departmentFilter);
      if (searchTerm && searchTerm.trim()) params.set('search', searchTerm.trim());
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const response = await fetch(`${API_BASE_URL}/relieving-letter?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-user-role': userRole,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('Failed to load relieving letters');
      }

      const result = await response.json();
      const data = result.data || {};
      const pagination = data.pagination || {};

      setRelievingLetters(data.letters || []);
      setLettersTotalPages(pagination.totalPages || 1);
      setLettersTotalItems(pagination.totalItems || 0);

      return { letters: data.letters || [], pagination };
    },
    enabled: true,
    refetchInterval: 0,
  });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedRelievingLetters = useMemo(() => {
    if (!sortConfig.key) return relievingLetters;

    return [...relievingLetters].sort((a, b) => {
      let aVal, bVal;

      if (sortConfig.key === 'lastWorkingDate') {
        aVal = a.employmentDetails?.lastWorkingDate || '';
        bVal = b.employmentDetails?.lastWorkingDate || '';
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }

      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [relievingLetters, sortConfig]);

  // React Query: Load employees with approved exit requests from exit-test endpoint
  const { data: employeesData, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['relievingLetterEmployees', debouncedEmployeeSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('status', 'approved');
      params.set('limit', '1000'); // Get all approved exit requests
      if (debouncedEmployeeSearch && debouncedEmployeeSearch.trim()) {
        params.set('search', debouncedEmployeeSearch.trim());
      }

      const authToken = (typeof window !== 'undefined' && localStorage.getItem('auth_token')) || null;
      const headers = {
        'Content-Type': 'application/json',
        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
      };

      const response = await fetch(`${API_BASE_URL}/exit-test?${params.toString()}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to load employees');
      }

      const result = await response.json();
      const exitRequests = result.data?.exitRequests || [];

      // Extract unique employees from exit requests and store exit request data with each employee
      const employeeMap = new Map();
      exitRequests.forEach((exitRequest) => {
        const employee = exitRequest.employeeId;
        if (employee && employee._id) {
          const employeeId = employee._id.toString();
          if (!employeeMap.has(employeeId)) {
            // Format employee data similar to the previous structure
            const firstName = employee.personalInfo?.firstName || '';
            const lastName = employee.personalInfo?.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            const employeeCode = employee.employeeId || employeeId;
            const department = employee.employmentInfo?.department || '';
            const email = employee.contactInfo?.personalEmail || '';

            employeeMap.set(employeeId, {
              _id: employee._id,
              employeeId: employeeCode,
              id: employee._id,
              fullName: fullName,
              firstName: firstName,
              lastName: lastName,
              employeeName: fullName,
              personalInfo: {
                firstName: firstName,
                lastName: lastName
              },
              employmentInfo: {
                department: department
              },
              contactInfo: {
                personalEmail: email
              },
              department: department,
              email: email,
              // Store the exit request data for auto-filling
              exitRequestData: exitRequest
            });
          }
        }
      });

      const employees = Array.from(employeeMap.values());
      setEmployees(employees);
      return employees;
    },
    enabled: showLetterForm,
  });

  // React Query: Load exit requests for selected employee from exit-test endpoint
  const { data: exitRequestsData } = useQuery({
    queryKey: ['relievingLetterExitRequests', selectedEmployeeCode || letterForm.employeeId],
    queryFn: async () => {
      const empParam = selectedEmployeeCode || letterForm.employeeId;
      if (!empParam) return [];

      const authToken = (typeof window !== 'undefined' && localStorage.getItem('auth_token')) || null;
      const headers = {
        'Content-Type': 'application/json',
        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
      };

      // Search for exit requests by employee ID or employee code
      const params = new URLSearchParams();
      params.set('status', 'approved');
      params.set('limit', '100');
      params.set('search', empParam); // Search by employee ID or name

      const response = await fetch(`${API_BASE_URL}/exit-test?${params.toString()}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to load exit requests');
      }

      const result = await response.json();
      const exitRequests = result.data?.exitRequests || [];

      // Filter to match the exact employee
      const filteredRequests = exitRequests.filter((er) => {
        const empId = er.employeeId?._id?.toString() || er.employeeId?.id?.toString();
        const empCode = er.employeeId?.employeeId || '';
        return empId === empParam || empCode === empParam || er.employeeId?._id?.toString() === empParam;
      });

      setExitRequests(filteredRequests);
      return filteredRequests;
    },
    enabled: !!(selectedEmployeeCode || letterForm.employeeId),
  });

  // React Query: Load no-dues clearances (completed only)
  const { data: noDuesData } = useQuery({
    queryKey: ['relievingLetterNoDues'],
    queryFn: async () => {
      const qs = `?status=completed`;
      const response = await fetch(`${API_BASE_URL}/relieving-letter/no-dues${qs}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': userRole
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load no-dues clearances');
      }

      const result = await response.json();
      const noDuesClearances = result.data?.noDuesClearances || [];
      setNoDuesClearances(noDuesClearances);
      return noDuesClearances;
    },
    enabled: showLetterForm,
  });

  // Check backend status
  const checkBackendStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setBackendStatus('connected');
      } else {
        setBackendStatus('disconnected');
      }
    } catch (error) {
      setBackendStatus('disconnected');
    }
  };

  // Generate sample data
  const generateSampleData = async () => {
    try {

      // Check if backend is running first
      const healthCheck = await fetch(`${API_BASE_URL}/health`);
      if (!healthCheck.ok) {
        alert('❌ Backend server is not running. Please start the backend server first.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/relieving-letter/generate-sample-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': 'super_admin',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        alert('✅ Sample data generated successfully! You can now create relieving letters.');

        // Invalidate all queries to refetch data
        queryClient.invalidateQueries(['relievingLetters']);
        queryClient.invalidateQueries(['relievingLetterEmployees']);
        queryClient.invalidateQueries(['relievingLetterExitRequests']);
        queryClient.invalidateQueries(['relievingLetterNoDues']);
        queryClient.invalidateQueries(['relievingLetterStatistics']);
      } else {
        const errorText = await response.text();
        alert('❌ Failed to generate sample data. Please try again.');
      }
    } catch (error) {
      if (error.message.includes('fetch')) {
        alert('❌ Cannot connect to backend server. Please ensure the backend is running on port 3001.');
      } else {
        alert('❌ Error generating sample data. Please try again.');
      }
    }
  };

  // React Query: Load statistics
  const { data: statisticsData } = useQuery({
    queryKey: ['relievingLetterStatistics'],
    queryFn: async () => {
      // Use the same endpoint as letters list but with a high limit to get all letters for statistics
      const response = await fetch(`${API_BASE_URL}/relieving-letter?page=1&limit=1000`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-user-role': userRole,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('Failed to load statistics');
      }

      const result = await response.json();
      const letters = result.data?.letters || [];

      const stats = {
        totalLetters: letters.length,
        sentLetters: letters.filter(l => l.status === 'sent' || l.status === 'approved' || l.status === 'issued').length,
        pendingLetters: letters.filter(l => l.status === 'pending' || l.status === 'pending_approval').length,
        thisMonthLetters: letters.filter(l => {
          const letterDate = new Date(l.createdAt);
          const now = new Date();
          return letterDate.getMonth() === now.getMonth() && letterDate.getFullYear() === now.getFullYear();
        }).length
      };

      setLetterStats(stats);
      return stats;
    },
    enabled: true,
    refetchInterval: 0
  });

  // React Query: Load locations for Work Location dropdown and PDF generation (with full address)
  const { data: locationsData, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: async () => {
      const result = await locationService.getLocations({ isActive: 'all', limit: 1000 });
      if (result.success) {
        let locationsList = [];
        // Handle different response structures
        if (result.data?.data?.locations && Array.isArray(result.data.data.locations)) {
          locationsList = result.data.data.locations;
        } else if (result.data?.data && Array.isArray(result.data.data)) {
          locationsList = result.data.data;
        } else if (result.data?.locations && Array.isArray(result.data.locations)) {
          locationsList = result.data.locations;
        } else if (Array.isArray(result.data)) {
          locationsList = result.data;
        }
        setLocations(locationsList);
        return locationsList;
      }
      throw new Error(result.message || 'Failed to load locations');
    },
    enabled: true, // Always load locations so they're available for PDF generation
    staleTime: 0, // Cache for 5 minutes
  });

  // React Query: Load 50 employees by default for Reporting Manager dropdown
  const { data: defaultEmployeesData, isLoading: isLoadingDefaultEmployees } = useQuery({
    queryKey: ['reportingManagerEmployees', 'default'],
    queryFn: async () => {
      const authToken = (typeof window !== 'undefined' && localStorage.getItem('auth_token')) || null;
      const headers = authToken
        ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }
        : { 'Content-Type': 'application/json' };

      const url = `${API_BASE_URL}/dropdown-data/employees?limit=50`;
      const response = await fetch(url, { method: 'GET', headers });

      if (!response.ok) {
        throw new Error('Failed to load employees');
      }

      const result = await response.json();
      const employeesList = result?.data?.employees || [];
      setAllEmployees(employeesList);
      return employeesList;
    },
    enabled: showLetterForm && (!debouncedReportingManagerSearch || !debouncedReportingManagerSearch.trim()),
    staleTime: 0, // Cache for 5 minutes
  });

  // React Query: Search employees for Reporting Manager dropdown
  const { data: searchedEmployeesData, isLoading: isLoadingSearchedEmployees } = useQuery({
    queryKey: ['reportingManagerEmployees', 'search', debouncedReportingManagerSearch],
    queryFn: async () => {
      if (!debouncedReportingManagerSearch || !debouncedReportingManagerSearch.trim()) {
        return [];
      }

      const authToken = (typeof window !== 'undefined' && localStorage.getItem('auth_token')) || null;
      const headers = authToken
        ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }
        : { 'Content-Type': 'application/json' };

      const url = `${API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(debouncedReportingManagerSearch.trim())}`;
      const response = await fetch(url, { method: 'GET', headers });

      if (!response.ok) {
        throw new Error('Failed to search employees');
      }

      const result = await response.json();
      const employeesList = result?.data?.employees || [];
      setAllEmployees(employeesList);
      return employeesList;
    },
    enabled: showLetterForm && !!debouncedReportingManagerSearch && debouncedReportingManagerSearch.trim().length > 0,
    staleTime: 0,
  });

  // Determine which employees to show and loading state
  const reportingManagerEmployees = debouncedReportingManagerSearch && debouncedReportingManagerSearch.trim()
    ? (searchedEmployeesData || [])
    : (defaultEmployeesData || []);
  const isLoadingReportingManager = debouncedReportingManagerSearch && debouncedReportingManagerSearch.trim()
    ? isLoadingSearchedEmployees
    : isLoadingDefaultEmployees;

  // React Query: Load signatories (HR Manager/Super Admin/Sales Manager)
  const { data: signatoriesData, isLoading: isLoadingSignatories } = useQuery({
    queryKey: ['signatories', debouncedSignatorySearch],
    queryFn: async () => {
      const authToken = (typeof window !== 'undefined' && localStorage.getItem('auth_token')) || null;
      const headers = authToken
        ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }
        : { 'Content-Type': 'application/json' };

      // We'll fetch from two sources to ensure we get a comprehensive list by default
      // 1. Specific managers endpoint (usually returns all managers)
      // 2. Search endpoint if search term exists

      const endpoints = [
        `${API_BASE_URL}/hr/managers`,
        `${API_BASE_URL}/dropdown-data/employees?limit=200`
      ];
      if (debouncedSignatorySearch && debouncedSignatorySearch.trim()) {
        endpoints.push(`${API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(debouncedSignatorySearch)}&limit=100`);
      }

      const results = await Promise.allSettled(
        endpoints.map(url => fetch(url, { method: 'GET', headers }).then(res => res.json()))
      );

      let combinedList = [];
      results.forEach(res => {
        if (res.status === 'fulfilled' && res.value?.data) {
          const data = res.value.data;
          // Robustly flatten data from different possible API structures
          const items = data.employees ||
            data.managers ||
            data.docs ||
            data.data ||
            (Array.isArray(data) ? data : []);

          if (Array.isArray(items)) {
            combinedList = [...combinedList, ...items];
          }
        }
      });

      // Deduplicate by ID
      const uniqueMap = new Map();
      combinedList.forEach(emp => {
        const id = emp._id || emp.id;
        if (id && !uniqueMap.has(id)) {
          uniqueMap.set(id, emp);
        }
      });

      const list = Array.from(uniqueMap.values());

      // Strictly filter for authorized roles: HR Manager, Super Admin, Sales Manager
      return list.filter(emp => {
        if (!emp) return false;
        const role = String(emp.role || emp.userRole || '').toLowerCase().replace(/ /g, '_');

        return role === 'hr_manager' ||
          role === 'super_admin' ||
          role === 'sales_manager' ||
          role === 'sale_manager';
      });
    },
    enabled: showLetterForm,
    staleTime: 300000,
  });

  const signatories = signatoriesData || [];

  // Debounce signatory search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSignatorySearch(signatorySearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [signatorySearchTerm]);

  // Debounce employee search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // Debounce reporting manager search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReportingManagerSearch(reportingManagerSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [reportingManagerSearchTerm]);

  // Debounce search term - useUrlTableState handles debouncing internally

  // System status is now handled by React Query automatically

  // React Query: Create relieving letter mutation
  const createLetterMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await fetch(`${API_BASE_URL}/relieving-letter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-user-role': userRole,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        mode: 'cors',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create relieving letter';
        try {
          const errorData = await response.json();
          errorMessage = errorData.responseMessage || errorData.message || errorMessage;
        } catch (e) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['relievingLetters']);
      queryClient.invalidateQueries(['relievingLetterStatistics']);
      alert('✅ Relieving letter created successfully!');
      setShowLetterForm(false);
      resetLetterForm();
    },
    onError: (error) => {
      const errorMessage = error.status === 409
        ? 'Relieving letter already exists for this employee.'
        : 'Failed to create relieving letter: ' + error.message;
      alert(errorMessage);
    },
  });

  // React Query: Update relieving letter mutation
  const updateLetterMutation = useMutation({
    mutationFn: async ({ id, formData }) => {
      const response = await fetch(`${API_BASE_URL}/relieving-letter/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-user-role': userRole,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        mode: 'cors',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to update relieving letter';
        try {
          const errorData = await response.json();
          errorMessage = errorData.responseMessage || errorData.message || errorMessage;
        } catch (e) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['relievingLetters']);
      queryClient.invalidateQueries(['relievingLetterStatistics']);
      alert('✅ Relieving letter updated successfully!');
      setShowLetterForm(false);
      resetLetterForm();
    },
    onError: (error) => {
      alert('Failed to update relieving letter: ' + error.message);
    },
  });

  // Create relieving letter handler
  const handleSubmitLetter = async (e) => {
    e.preventDefault();

    // Enterprise-level validation
    const ok = validateAll();
    if (!ok) {
      setLetterTouchedFields(new Set([
        'employeeId',
        'employmentDetails.joinDate',
        'employmentDetails.lastWorkingDate',
        'employmentDetails.employmentType',
        'employmentDetails.workLocation',
        'employmentDetails.reasonForLeaving',
        'employmentDetails.reasonDescription',
        'financialDetails.lastSalary',
        'resignationDate',
        'acceptanceDate',
        'performanceDetails.overallRating',
        'authorization.signatoryId'
      ]));
      alert('❌ Please fix highlighted errors before submitting');
      return;
    }

    // Prepare form data
    const { noDuesClearanceId, ...restFormData } = letterForm;

    // Ensure issueDate is populated (fall back to today's date if empty)
    const finalIssueDate = restFormData.issueDate || new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    // Ensure letter body and salutation are never empty during save
    const currentBody = restFormData.letterContent?.body;
    const finalLetterContent = {
      ...restFormData.letterContent,
      body: (!currentBody || String(currentBody).trim() === '') ? generateDefaultLetterBody(letterForm, employees) : currentBody,
      salutation: restFormData.letterContent?.salutation || 'To Whom It May Concern,'
    };

    const formDataToSend = {
      ...restFormData,
      issueDate: finalIssueDate,
      letterContent: finalLetterContent,
      performanceDetails: {
        ...restFormData.performanceDetails,
        skills: typeof restFormData.performanceDetails.skills === 'string'
          ? restFormData.performanceDetails.skills.split(',').map(s => s.trim()).filter(Boolean)
          : restFormData.performanceDetails.skills
      },
      ...(noDuesClearanceId && (typeof noDuesClearanceId === 'string' ? noDuesClearanceId.trim() !== '' : (noDuesClearanceId._id || noDuesClearanceId.id || true)) ? { noDuesClearanceId: noDuesClearanceId._id || noDuesClearanceId.id || noDuesClearanceId } : {})
    };
    if (editingLetterId) {
      updateLetterMutation.mutate({ id: editingLetterId, formData: formDataToSend });
    } else {
      createLetterMutation.mutate(formDataToSend);
    }
  };

  // Update letter status (real data only) - SAME FORMAT AS EXPERIENCE CERTIFICATE
  const handleStatusUpdate = async (letterId, newStatus) => {
    try {

      // Use API endpoint
      const response = await fetch(`${API_BASE_URL}/relieving-letter/${letterId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-user-role': userRole
        },
        mode: 'cors',
        body: JSON.stringify({
          status: newStatus,
          comments: `Status updated to ${newStatus}`
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Relieving letter status updated to ${newStatus}!`);
        queryClient.invalidateQueries(['relievingLetters']);
        queryClient.invalidateQueries(['relievingLetterStatistics']);
      } else {
        const errorResult = await response.json();
        alert('❌ Failed to update relieving letter status: ' + (errorResult.message || 'Unknown error'));
      }
    } catch (error) {
      alert('❌ Failed to update relieving letter status: ' + error.message);
    }
  };


  // Issue letter (real data only) - SAME FORMAT AS EXPERIENCE CERTIFICATE
  const handleIssueLetter = async (letterId) => {
    try {

      // Use API endpoint
      const response = await fetch(`${API_BASE_URL}/relieving-letter/${letterId}/issue`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-user-role': userRole,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        mode: 'cors',
        body: JSON.stringify({
          deliveryMethod: 'email',
          emailAddress: 'employee@company.com'
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Relieving letter issued!`);
        queryClient.invalidateQueries(['relievingLetters']);
        queryClient.invalidateQueries(['relievingLetterStatistics']);
      } else {
        const errorResult = await response.json();
        alert('❌ Failed to issue relieving letter: ' + (errorResult.message || 'Unknown error'));
      }
    } catch (error) {
      alert('❌ Failed to issue relieving letter: ' + error.message);
    }
  };

  // Show delete confirmation modal
  const handleDeleteLetter = (letter) => {
    setLetterToDelete(letter);
    setShowDeleteModal(true);
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setLetterToDelete(null);
    setIsDeleting(false);
  };

  // Actually delete letter (called from modal)
  const handleConfirmDelete = async () => {
    if (!letterToDelete) {
      return;
    }

    const letterId = letterToDelete._id;
    setIsDeleting(true);

    try {

      // Use API endpoint
      const response = await fetch(`${API_BASE_URL}/relieving-letter/${letterId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-user-role': userRole,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        mode: 'cors'
      });

      if (response.ok) {
        alert('Relieving letter deleted successfully!');
        queryClient.invalidateQueries(['relievingLetters']);
        queryClient.invalidateQueries(['relievingLetterStatistics']);
        handleCloseDeleteModal();
      } else {
        const errorResult = await response.json();
        alert('Failed to delete relieving letter: ' + (errorResult.responseMessage || errorResult.message || 'Unknown error'));
        setIsDeleting(false);
      }
    } catch (error) {
      alert('Failed to delete relieving letter: ' + error.message);
      setIsDeleting(false);
    }
  };

  // Bulk delete relieving letters handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.size} relieving letter(s)? This action cannot be undone.`)) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedRows);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process deletions in parallel batches of 5 to speed up but not overwhelm
      const batchSize = 5;
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        await Promise.all(batch.map(async (id) => {
          try {
            const response = await fetch(`${API_BASE_URL}/relieving-letter/${id}`, {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-user-role': userRole,
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
              },
              mode: 'cors'
            });
            if (response.ok) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (err) {
            errorCount++;
          }
        }));
      }

      if (successCount > 0) {
        alert(`Successfully deleted ${successCount} relieving letter(s)`);
        setSelectedRows(new Set());
        queryClient.invalidateQueries(['relievingLetters']);
        queryClient.invalidateQueries(['relievingLetterStatistics']);
      }

      if (errorCount > 0) {
        alert(`Failed to delete ${errorCount} relieving letter(s). Please try again.`);
      }
    } finally {
      setIsBulkDeleting(false);
    }
  };


  // Generate default letter body from form data
  const generateDefaultLetterBody = (formData, employeesList) => {
    // Attempt to find the employee in the provided list
    const employee = (employeesList || []).find(e => {
      const value = e.employeeId || e.id || e._id;
      return String(value) === String(formData.employeeId);
    });

    // Fall back to stored fields if employee object is not found in the list
    const name = employee?.fullName || formData.employeeName || 'Employee';
    const id = employee?.employeeId || employee?._id || formData.employeeIdNumber || 'N/A';
    const designation = employee?.employmentInfo?.designation || formData.position || '';
    const dept = employee?.employmentInfo?.department || formData.department || '';

    // Format dates nicely
    const formatDateNice = (dateStr) => {
      if (!dateStr) return 'N/A';
      try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return 'N/A';
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      } catch (e) { return 'N/A'; }
    };

    const joinDate = formatDateNice(formData.employmentDetails?.joinDate);
    const lwd = formatDateNice(formData.employmentDetails?.lastWorkingDate);

    const companyName = process.env.REACT_APP_COMPANY_NAME;
    const positionText = designation && String(designation).toLowerCase() !== 'employee' ? `as a ${designation}` : 'as an Employee';
    const deptText = dept ? `in the ${dept} Department` : '';

    let body = `This is to certify that ${name} (Employee ID: ${id}) was employed with ${companyName} ${positionText} ${deptText}.\n\n`;
    body += `${name} joined the organization on ${joinDate}, and their last working day was ${lwd}. During this period, they were employed on a ${formData.employmentDetails?.employmentType || 'full time'} basis.\n\n`;
    body += `During their tenure, ${name} performed their responsibilities with professionalism and sincerity. They have been relieved of all duties and responsibilities from the organization effective ${lwd}.\n\n`;

    const manager = formData.employmentDetails?.reportingManager;
    if (manager) {
      const managerDisplayName = typeof manager === 'object' ? manager.name || manager.employeeName || 'N/A' : manager;
      if (managerDisplayName && managerDisplayName !== 'N/A') {
        body += `Reporting Manager: ${managerDisplayName}\n\n`;
      }
    }

    body += `We wish ${name} success in all their future endeavors.`;

    return body;
  };

  // Preview letter from form
  const handlePreviewLetter = () => {
    const ok = validateAll();
    if (!ok) {
      setLetterTouchedFields(new Set([
        'employeeId',
        'employmentDetails.joinDate',
        'employmentDetails.lastWorkingDate',
        'employmentDetails.employmentType',
        'employmentDetails.workLocation',
        'employmentDetails.reasonForLeaving',
        'employmentDetails.reasonDescription',
        'financialDetails.lastSalary',
        'resignationDate',
        'acceptanceDate',
        'performanceDetails.overallRating',
        'authorization.signatoryId'
      ]));
      alert('❌ Please fix highlighted errors before previewing');
      return;
    }

    if (!letterForm.employeeId) {
      alert('Please select an employee first');
      return;
    }

    const employee = employees.find(e => {
      const value = e.employeeId || e.id || e._id;
      return String(value) === String(letterForm.employeeId);
    });

    // Ensure currentBody is defined (either from form or default generation)
    let currentBody = letterForm.letterContent?.body;
    if (!currentBody || currentBody.trim() === '') {
      currentBody = generateDefaultLetterBody(letterForm, employees);
    }

    // Ensure issueDate is defined
    const currentIssueDate = letterForm.issueDate || new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    // Update form state for consistency (background update)
    setLetterForm(prev => ({
      ...prev,
      issueDate: currentIssueDate,
      letterContent: {
        ...prev.letterContent,
        body: currentBody,
        subject: prev.letterContent?.subject || '',
        salutation: prev.letterContent?.salutation || 'To Whom It May Concern,',
        closing: prev.letterContent?.closing || '',
        companyName: process.env.REACT_APP_COMPANY_NAME,
        companyAddress: prev.letterContent?.companyAddress || locations.find(l => (l.name || l.label) === prev.employmentDetails.workLocation)?.address?.fullAddress || ''
      }
    }));

    const previewData = {
      ...letterForm,
      issueDate: currentIssueDate,
      employeeName: employee?.fullName || 'Employee',
      employeeIdNumber: employee?.employeeId || employee?._id || 'N/A',
      employeeEmail: employee?.email || employee?.personalEmail || '',
      department: employee?.department || letterForm.department || '',
      letterContent: {
        ...letterForm.letterContent,
        body: currentBody,
        salutation: letterForm.letterContent?.salutation || 'To Whom It May Concern,',
      }
    };

    setPreviewLetter(previewData);
    setShowPreview(true);
  };

  // View letter details - Navigate to detail page

  const handleViewLetter = (letter) => {
    if (letter?._id) {
      navigate(`/relieving_letter/${letter._id}`);
    } else {
    }
  };

  // Edit letter handler
  const handleEditLetter = (letter) => {
    setLetterForm({
      employeeId: letter.employeeId?._id || letter.employeeIdNumber || letter.employeeId?.employeeId || letter.employeeId || '',
      employeeName: letter.employeeName || (letter.employeeId && resolveEmployeeName(letter.employeeId)) || '',
      employeeIdNumber: letter.employeeIdNumber || letter.employeeId?.employeeId || '',
      exitRequestId: letter.exitRequestId?._id || letter.exitRequestId || '',
      noDuesClearanceId: letter.noDuesClearanceId?._id || letter.noDuesClearanceId || '',
      resignationDate: letter.resignationDate ? new Date(letter.resignationDate).toISOString().split('T')[0] : '',
      acceptanceDate: letter.acceptanceDate ? new Date(letter.acceptanceDate).toISOString().split('T')[0] : '',
      noticePeriodDays: letter.noticePeriodDays || 30,
      noticeServedDays: letter.noticeServedDays || 0,
      noticeShortfallDays: letter.noticeShortfallDays || 0,
      noticeBuyoutAmount: letter.noticeBuyoutAmount || 0,
      exitType: letter.exitType || 'voluntary',
      separationCategory: letter.separationCategory || '',
      noDuesStatus: letter.noDuesStatus || 'pending',
      fnfStatus: letter.fnfStatus || 'pending',
      letterNumber: letter.letterNumber || '',
      versionNumber: letter.versionNumber || 1,
      issueDate: letter.issueDate || '',
      status: letter.status || 'draft',
      authorization: {
        signatoryId: letter.authorization?.signatoryId || '',
        name: letter.authorization?.name || '',
        email: letter.authorization?.email || '',
        role: letter.authorization?.role || '',
        department: letter.authorization?.department || '',
        digitalSignatureUrl: letter.authorization?.digitalSignatureUrl || '',
        companySealUrl: letter.authorization?.companySealUrl || ''
      },
      employmentDetails: {
        joinDate: letter.employmentDetails?.joinDate ? new Date(letter.employmentDetails.joinDate).toISOString().split('T')[0] : '',
        lastWorkingDate: letter.employmentDetails?.lastWorkingDate ? new Date(letter.employmentDetails.lastWorkingDate).toISOString().split('T')[0] : '',
        employmentType: letter.employmentDetails?.employmentType || 'full-time',
        workLocation: letter.employmentDetails?.workLocation || '',
        reportingManager: typeof letter.employmentDetails?.reportingManager === 'string' ? { name: letter.employmentDetails?.reportingManager } : letter.employmentDetails?.reportingManager || { employeeId: '', name: '', email: '', role: '', department: '' },
        reasonForLeaving: letter.employmentDetails?.reasonForLeaving || 'resignation',
        reasonDescription: letter.employmentDetails?.reasonDescription || ''
      },
      performanceDetails: {
        overallRating: letter.performanceDetails?.overallRating || 'good',
        keyAchievements: letter.performanceDetails?.keyAchievements || [],
        skills: letter.performanceDetails?.skills?.join(', ') || '',
        performanceNotes: letter.performanceDetails?.performanceNotes || ''
      },
      financialDetails: {
        lastSalary: letter.financialDetails?.lastSalary || 0,
        currency: letter.financialDetails?.currency || 'INR',
        benefits: letter.financialDetails?.benefits || []
      },
      letterContent: {
        subject: letter.letterContent?.subject || '',
        salutation: letter.letterContent?.salutation || 'To Whom It May Concern,',
        body: letter.letterContent?.body || '',
        closing: letter.letterContent?.closing || 'Yours sincerely,',
        signature: letter.letterContent?.signature || '',
        designation: letter.letterContent?.designation || '',
        companyName: letter.letterContent?.companyName || '',
        companyAddress: letter.letterContent?.companyAddress || ''
      },
      priority: letter.priority || 'medium'
    });
    setEditingLetterId(letter._id || letter.id);
    setShowLetterForm(true);
    setLetterTouchedFields(new Set());
    setFormErrors({});
    setSelectedSignatory({
      ...letter.authorization,
      employeeId: letter.letterContent?.signature && letter.authorization?.signatoryId === letter.employeeId?.employeeId ? letter.employeeId?.employeeId : (letter.authorization?.signatoryId || 'N/A')
    });
  };


  // Reset form
  const resetLetterForm = () => {
    setLetterForm({
      employeeId: '',
      employeeName: '',
      employeeIdNumber: '',
      exitRequestId: '',
      noDuesClearanceId: '',
      resignationDate: '',
      acceptanceDate: '',
      noticePeriodDays: 30, // Default to 30
      noticeServedDays: 0,
      noticeShortfallDays: 0,
      noticeBuyoutAmount: 0,
      exitType: 'voluntary',
      separationCategory: '',
      noDuesStatus: 'pending',
      fnfStatus: 'pending',
      letterNumber: '',
      versionNumber: 1,
      issueDate: '',
      status: 'draft',
      authorization: {
        signatoryId: '',
        name: '',
        email: '',
        role: '',
        department: '',
        digitalSignatureUrl: '',
        companySealUrl: ''
      },
      employmentDetails: {
        joinDate: '',
        lastWorkingDate: '',
        employmentType: 'full-time',
        workLocation: '',
        reportingManager: {
          employeeId: '',
          name: '',
          email: '',
          role: '',
          department: ''
        },
        reasonForLeaving: 'resignation',
        reasonDescription: ''
      },
      performanceDetails: {
        overallRating: 'good',
        keyAchievements: [],
        skills: '',
        performanceNotes: ''
      },
      financialDetails: {
        lastSalary: 0,
        currency: 'INR',
        benefits: [],
      },
      letterContent: {
        subject: '',
        salutation: 'To Whom It May Concern,',
        body: '',
        closing: 'Yours sincerely,',
        signature: '',
        designation: '',
        companyName: '',
        companyAddress: ''
      },
      priority: 'medium'
    });
    setFormErrors({});
    setLetterTouchedFields(new Set());
    setEditingLetterId(null);
    setSelectedSignatory(null);
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      pending_approval: 'bg-yellow-100 text-yellow-800', // Legacy support
      sent: 'bg-green-100 text-green-800',
      approved: 'bg-green-100 text-green-800', // Legacy support
      issued: 'bg-green-100 text-green-800' // Legacy support
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="h-4 w-4" />,
      pending_approval: <Clock className="h-4 w-4" />, // Legacy support
      sent: <Send className="h-4 w-4" />,
      approved: <Send className="h-4 w-4" />, // Legacy support
      issued: <Send className="h-4 w-4" /> // Legacy support
    };
    return icons[status] || <AlertCircle className="h-4 w-4" />;
  };

  // Filtering is now handled by backend through React Query

  // Test API connectivity
  const testAPIConnectivity = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        mode: 'cors'
      });
      // Do not parse JSON; some environments return HTML or plain text
      return true; // never block loads based on this
    } catch (error) {
      return true; // proceed anyway; each loader has its own error handling
    }
  };




  // When employee changes: set code/id, refetch dependent lists, and auto-fill employment basics
  useEffect(() => {
    if (!letterForm.employeeId) return;
    const emp = employees.find((e) => (e._id === letterForm.employeeId) || (e.employeeId === letterForm.employeeId) || (e.id === letterForm.employeeId));
    if (emp) {
      // Store both to support filters
      setSelectedEmployeeCode(emp.employeeId || emp.id);
      // Auto-fill employment details
      setLetterForm((prev) => ({
        ...prev,
        employmentDetails: {
          ...prev.employmentDetails,
          joinDate: emp.joinDate ? String(emp.joinDate).slice(0, 10) : prev.employmentDetails.joinDate,
          employmentType: emp.employmentType || prev.employmentDetails.employmentType,
          workLocation: (() => { const l = emp?.workLocation?.fromLocation || emp?.workLocation || emp?.location || emp?.employmentInfo?.workLocation; return typeof l === 'object' && l ? (l.name || l.label || l.value || '') : (l || prev.employmentDetails.workLocation); })()
        }
      }));
    }
  }, [letterForm.employeeId, employees]);

  // (Removed redundant useEffect that mistakenly overwrote letterForm values when exitRequestId changed)

  // When No-Dues selected, auto-fill employment + financials and set employee/exitRequest
  useEffect(() => {
    if (!letterForm.noDuesClearanceId) return;
    const nd = noDuesClearances.find((n) => (n._id || n.id) === letterForm.noDuesClearanceId);
    if (nd) {
      // Set employee and exit request from selected no-dues
      setLetterForm((prev) => {
        const resignationDate = nd.exitRequest?.resignationDate ? String(new Date(nd.exitRequest.resignationDate).toISOString().slice(0, 10)) : (nd.exitRequest?.submittedDate ? String(new Date(nd.exitRequest.submittedDate).toISOString().slice(0, 10)) : (nd.exitRequest?.createdAt ? String(new Date(nd.exitRequest.createdAt).toISOString().slice(0, 10)) : prev.resignationDate));
        const lastWorkingDate = nd.exitRequest?.lastWorkingDate ? String(new Date(nd.exitRequest.lastWorkingDate).toISOString().slice(0, 10)) : (nd.lastWorkingDate ? String(new Date(nd.lastWorkingDate).toISOString().slice(0, 10)) : prev.employmentDetails.lastWorkingDate);

        // Calculate notice period served logic
        let noticeServedDays = prev.noticeServedDays || 0;
        if (resignationDate && lastWorkingDate && resignationDate !== prev.resignationDate) {
          const res = new Date(resignationDate);
          const lwd = new Date(lastWorkingDate);
          noticeServedDays = Math.max(0, Math.ceil((lwd - res) / (1000 * 60 * 60 * 24)));
        }

        return {
          ...prev,
          employeeId: nd.employeeDbId || prev.employeeId,
          exitRequestId: nd.exitRequestId || prev.exitRequestId,
          resignationDate: resignationDate || prev.resignationDate,
          noDuesStatus: nd.clearanceStatus === 'completed' ? 'approved' : 'pending',
          fnfStatus: nd.fnfStatus || prev.fnfStatus || 'pending',
          acceptanceDate: nd.exitRequest?.approvedDate ? String(new Date(nd.exitRequest.approvedDate).toISOString().slice(0, 10)) : (nd.exitRequest?.hrApproval?.timestamp ? String(new Date(nd.exitRequest.hrApproval.timestamp).toISOString().slice(0, 10)) : (nd.exitRequest?.managerApproval?.timestamp ? String(new Date(nd.exitRequest.managerApproval.timestamp).toISOString().slice(0, 10)) : prev.acceptanceDate)),
          employmentDetails: {
            ...prev.employmentDetails,
            joinDate: nd.employee?.joinDate ? String(new Date(nd.employee.joinDate).toISOString().slice(0, 10)) : prev.employmentDetails.joinDate,
            employmentType: nd.employee?.employmentType || prev.employmentDetails.employmentType,
            workLocation: (() => { const l = nd.employee?.workLocation?.fromLocation || nd.employee?.workLocation || nd.employee?.location || nd.employee?.employmentInfo?.workLocation; return typeof l === 'object' && l ? (l.name || l.label || l.value || '') : (l || prev.employmentDetails.workLocation); })(),
            lastWorkingDate: lastWorkingDate || prev.employmentDetails.lastWorkingDate,
            reasonForLeaving: nd.exitRequest?.resignationType || prev.employmentDetails.reasonForLeaving,
            reasonDescription: nd.exitRequest?.detailedReason || nd.exitRequest?.reason || prev.employmentDetails.reasonDescription
          },
          noticeServedDays,
          noticePeriodDays: nd.exitRequest?.noticePeriod || prev.noticePeriodDays || 30,
          exitType: nd.exitRequest?.resignationType === 'termination' ? 'termination' : (nd.exitRequest?.resignationCategory === 'Retirement' ? 'retirement' : (nd.exitRequest?.resignationType === 'voluntary' ? 'voluntary' : prev.exitType)),
        };
      });
    }
    if (nd && nd.financialClearance) {
      const fin = nd.financialClearance;
      const pendingDues = fin?.pendingSalary?.amount || 0;
      setLetterForm((prev) => ({
        ...prev,
        financialDetails: {
          ...prev.financialDetails,
          pendingDues,
        }
      }));
    }
  }, [letterForm.noDuesClearanceId, noDuesClearances.length]);

  // Auto-calculate Notice Served Days based on Resignation Date and Current Date
  useEffect(() => {
    if (!letterForm.resignationDate) return;

    const resDate = new Date(letterForm.resignationDate);
    const currentDate = new Date();

    // Reset time components for accurate day calculation
    resDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    const noticePeriod = parseInt(letterForm.noticePeriodDays) || 0;

    // Only calculate if valid date
    if (!isNaN(resDate.getTime())) {
      const diffTime = currentDate - resDate;
      // User said "based on resignation date and current date".
      // If resigned today, diff is 0. If resigned yesterday, diff is 1.
      // Typically notice includes the start date (inclusive).
      let servedDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1);

      // Cap at notice period
      if (servedDays > noticePeriod) {
        servedDays = noticePeriod;
      }

      if (letterForm.noticeServedDays !== servedDays) {
        setLetterForm(prev => ({ ...prev, noticeServedDays: servedDays }));
      }

    }
  }, [letterForm.resignationDate, letterForm.noticePeriodDays]); // Re-run if notice period changes to re-cap

  // Auto-calculate Notice Shortfall Days whenever Notice Period or Served Days changes
  useEffect(() => {
    const period = parseInt(letterForm.noticePeriodDays) || 0;
    const served = parseInt(letterForm.noticeServedDays) || 0;
    const shortfall = Math.max(0, period - served);

    if (letterForm.noticeShortfallDays !== shortfall) {
      setLetterForm(prev => ({ ...prev, noticeShortfallDays: shortfall }));
    }
  }, [letterForm.noticePeriodDays, letterForm.noticeServedDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep search box label in sync with selection
  useEffect(() => {
    const sel = noDuesClearances.find((c) => (c._id || c.id) === letterForm.noDuesClearanceId);
    if (sel) {
      const label = `${sel.employeeName || ''}${sel.clearanceId ? ` - ${sel.clearanceId}` : ''}`.trim();
      setNoDuesSearchTerm(label);
    }
  }, [letterForm.noDuesClearanceId, noDuesClearances.length]);

  // Close No-Dues dropdown on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (showNoDuesDropdown && noDuesDropdownRef.current && !noDuesDropdownRef.current.contains(e.target)) {
        setShowNoDuesDropdown(false);
      }
      if (isEmployeeDropdownOpen && employeeDropdownRef.current && !employeeDropdownRef.current.contains(e.target)) {
        setIsEmployeeDropdownOpen(false);
      }
      if (isReportingManagerDropdownOpen && reportingManagerDropdownRef.current && !reportingManagerDropdownRef.current.contains(e.target)) {
        setIsReportingManagerDropdownOpen(false);
      }
      if (isSignatoryDropdownOpen && signatoryDropdownRef.current && !signatoryDropdownRef.current.contains(e.target)) {
        setIsSignatoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showNoDuesDropdown, isEmployeeDropdownOpen, isReportingManagerDropdownOpen, isSignatoryDropdownOpen]);

  // Enterprise validation helpers
  const setFieldError = (key, message) => {
    setFormErrors((prev) => ({ ...prev, [key]: message || undefined }));
  };

  const validateField = (key, value) => {
    const v = typeof value === 'string' ? value.trim() : value;
    let error = '';
    switch (key) {
      case 'employeeId':
        error = v ? '' : 'Employee is required';
        break;
      case 'employmentDetails.joinDate':
        error = v ? '' : 'Join date is required';
        break;
      case 'employmentDetails.lastWorkingDate':
        error = v ? '' : 'Last working date is required';
        break;
      case 'employmentDetails.workLocation':
        error = v ? '' : 'Work location is required';
        break;
      case 'employmentDetails.reasonForLeaving':
        error = v ? '' : 'Reason for leaving is required';
        break;
      case 'employmentDetails.reasonDescription':
        error = v ? '' : 'Reason description is required';
        break;
      case 'resignationDate':
        error = v ? '' : 'Resignation date is required';
        break;
      case 'acceptanceDate':
        error = v ? '' : 'Acceptance date is required';
        break;
      case 'employmentDetails.employmentType':
        error = v ? '' : 'Employment type is required';
        break;
      case 'performanceDetails.overallRating':
        error = v ? '' : 'Overall rating is required';
        break;
      case 'financialDetails.lastSalary':
        error = (v > 0) ? '' : 'Net salary must be non-zero (ensure employee has salary info)';
        break;
      case 'authorization.signatoryId':
        error = v ? '' : 'Authorized signatory is required';
        break;
      default:
        break;
    }
    setFieldError(key, error);
    return error;
  };

  const validateAll = () => {
    const errors = {
      employeeId: validateField('employeeId', letterForm.employeeId),
      'employmentDetails.joinDate': validateField('employmentDetails.joinDate', letterForm.employmentDetails.joinDate),
      'employmentDetails.lastWorkingDate': validateField('employmentDetails.lastWorkingDate', letterForm.employmentDetails.lastWorkingDate),
      'employmentDetails.workLocation': validateField('employmentDetails.workLocation', letterForm.employmentDetails.workLocation),
      'employmentDetails.reasonForLeaving': validateField('employmentDetails.reasonForLeaving', letterForm.employmentDetails.reasonForLeaving),
      'employmentDetails.reasonDescription': validateField('employmentDetails.reasonDescription', letterForm.employmentDetails.reasonDescription),
      resignationDate: validateField('resignationDate', letterForm.resignationDate),
      acceptanceDate: validateField('acceptanceDate', letterForm.acceptanceDate),
      'employmentDetails.employmentType': validateField('employmentDetails.employmentType', letterForm.employmentDetails.employmentType),
      'performanceDetails.overallRating': validateField('performanceDetails.overallRating', letterForm.performanceDetails.overallRating),
      'financialDetails.lastSalary': validateField('financialDetails.lastSalary', letterForm.financialDetails.lastSalary),
      'authorization.signatoryId': validateField('authorization.signatoryId', letterForm.authorization.signatoryId),
    };

    setFormErrors(errors);
    const hasError = Object.values(errors).some(Boolean);
    return !hasError;
  };

  // Render dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Loading Indicator */}
      {isLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <RefreshCw className="h-5 w-5 animate-spin text-[#0F2942] mr-3" />
            <span className="text-blue-800 font-medium">Refreshing data...</span>
          </div>
        </div>
      )}

      {/* Dashboard Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Relieving Letter Dashboard</h2>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={async () => {
              try {
                setIsLoading(true);

                // Force refresh by clearing data first
                setRelievingLetters([]);
                setLetterStats({});
                setEmployees([]);
                setExitRequests([]);
                setNoDuesClearances([]);

                // Invalidate all queries to refetch data
                queryClient.invalidateQueries(['relievingLetters']);
                queryClient.invalidateQueries(['relievingLetterStatistics']);
                queryClient.invalidateQueries(['relievingLetterEmployees']);
                queryClient.invalidateQueries(['relievingLetterExitRequests']);
                queryClient.invalidateQueries(['relievingLetterNoDues']);

              } catch (error) {
                alert('❌ Failed to refresh data: ' + error.message);
              } finally {
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
            className={`flex items-center px-4 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-1 ${isLoading
              ? 'text-gray-400 bg-gray-200 border-gray-300 cursor-not-allowed'
              : 'text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200 focus:ring-gray-500'
              }`}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Refreshing...' : 'Refresh Data'}
          </button>
          <PermissionGate permission="exit_management" action="create">
            <button
              onClick={generateSampleData}
              className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Generate Sample Data
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-[#0F2942]" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Letters</p>
              <p className="text-2xl font-bold text-gray-900">{letterStats.totalLetters || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{letterStats.pendingLetters || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Send className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Sent</p>
              <p className="text-2xl font-bold text-gray-900">{letterStats.sentLetters || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Department Statistics */}
      {letterStats.departmentStats && letterStats.departmentStats.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Department Status</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {letterStats.departmentStats.map((dept, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900">{dept._id}</h4>
                  <p className="text-sm text-gray-500 mb-2">{dept.total} letters</p>
                  <div className="space-y-1">
                    {dept.draft > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Draft:</span>
                        <span className="font-medium">{dept.draft}</span>
                      </div>
                    )}
                    {dept.pendingApproval > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-yellow-600">Pending Approval:</span>
                        <span className="font-medium">{dept.pendingApproval}</span>
                      </div>
                    )}
                    {dept.approved > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#0F2942]">Approved:</span>
                        <span className="font-medium">{dept.approved}</span>
                      </div>
                    )}
                    {dept.issued > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600">Issued:</span>
                        <span className="font-medium">{dept.issued}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* System Information */}
      <div className="bg-green-50 rounded-lg border border-green-200 p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-green-900">System Status</h3>
            <div className="mt-2 text-sm text-green-700">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p><strong>Employees:</strong> {employees.length}</p>
                </div>
                <div>
                  <p><strong>Exit Requests:</strong> {exitRequests.length}</p>
                </div>
                <div>
                  <p><strong>No-Dues Clearances:</strong> {noDuesClearances.length}</p>
                </div>
                <div>
                  <p><strong>Letters:</strong> {relievingLetters.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Letters */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Letters</h3>
        </div>
        <div className="p-6">
          {relievingLetters && relievingLetters.length > 0 ? (
            <div className="space-y-4">
              {relievingLetters.slice(0, 5).map((letter) => (
                <div key={letter._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-600" />
                      </div>
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{letter.employeeName}</p>
                      <p className="text-sm text-gray-500 truncate capitalize">{letter.department} • {letter.employeeId?.employmentInfo?.designation || letter.position || 'N/A'}</p>
                      <p className="text-xs text-gray-400 truncate">ID: {letter.letterId}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(letter.status)}`}>
                      {getStatusIcon(letter.status)}
                      <span className="ml-1 capitalize">{letter.status}</span>
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewLetter(letter)}
                        className="text-[#0F2942] hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDownloadLetter(letter)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Download Letter"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No letters found</p>
              <p className="text-sm text-gray-400 mt-2">Create a letter to see recent activities</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );



  // Download letter as PDF
  const handleDownloadLetter = async (letter) => {
    try {

      const fileName = `Relieving_Letter_${letter.employeeName.replace(/\s+/g, '_')}_${letter.letterId}.pdf`;
      const userRole = localStorage.getItem('user_role') || 'hr_manager';

      await downloadFromBackend(
        `${API_BASE_URL}/relieving-letter/${letter._id}/download`,
        fileName,
        { 'x-user-role': userRole }
      );

    } catch (error) {
      alert(`❌ Failed to download relieving letter PDF: ${error.message || 'Server error occurred.'}`);
    }
  };

  // Show send confirmation modal
  const handleSendLetterClick = (letter) => {
    setLetterToSend(letter);
    setShowSendModal(true);
  };

  // Close send modal
  const handleCloseSendModal = () => {
    setShowSendModal(false);
    setLetterToSend(null);
    setIsSending(false);
  };

  // Actually send letter via email (called from modal)
  const handleSendLetter = async () => {
    if (!letterToSend) {
      return;
    }

    const letter = letterToSend;
    setIsSending(true);

    try {

      const authToken = localStorage.getItem('auth_token');
      const userRole = localStorage.getItem('user_role') || 'hr_manager';

      // Send to backend - PDF is now generated on backend for security and performance
      const response = await fetch(`${API_BASE_URL}/relieving-letter/${letter._id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'x-user-role': userRole,
          'x-user-id': localStorage.getItem('auth_user_id') || '',
          'x-user-name': localStorage.getItem('auth_user_name') || ''
        },
        mode: 'cors',
        body: JSON.stringify({
          emailContent: null // Use default email template
        })
      });


      const data = await response.json();

      if (response.ok && data.responseCode === 200) {

        // Close modal and refresh the letters list
        handleCloseSendModal();
        alert(`Relieving letter sent successfully to ${letter.employeeEmail || letter.employeeName}`);
        queryClient.invalidateQueries(['relievingLetters']);
      } else {
        alert(`Failed to send email: ${data.responseMessage || data.message || 'Unknown error'}`);
        setIsSending(false);
      }
    } catch (error) {
      alert('Failed to send relieving letter email: ' + error.message);
      setIsSending(false);
    }
  };

  // Render letter form
  const renderLetterForm = () => (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {editingLetterId ? 'Edit Relieving Letter' : 'Create Relieving Letter'}
          </h3>
          <div className="flex space-x-2">
            {/* Removed Generate Sample Data */}
            <button
              onClick={() => {
                setShowLetterForm(false);
                setSelectedLetter(null);
                resetLetterForm();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      <form className="p-6 space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          </div>
        </div>

        {/* Employment Details */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 border-b pb-2">Employment Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee <span className="text-red-500">*</span>
              </label>
              <div className="relative" ref={employeeDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    if (editingLetterId) return;
                    setIsEmployeeDropdownOpen((o) => !o);
                    if (!isEmployeeDropdownOpen) {
                      handleLetterFieldBlur('employeeId');
                      setFormErrors(prev => ({ ...prev, employeeId: validateField('employeeId', letterForm.employeeId) }));
                    }
                  }}
                  onBlur={() => {
                    if (editingLetterId) return;
                    handleLetterFieldBlur('employeeId');
                    // Slight delay to allow selection to happen if clicking an option
                    setTimeout(() => {
                      if (!isEmployeeDropdownOpen) {
                        setFormErrors(prev => ({ ...prev, employeeId: validateField('employeeId', letterForm.employeeId) }));
                      }
                    }, 200);
                  }}
                  disabled={!!editingLetterId}
                  className={`input-field text-left ${letterTouchedFields.has('employeeId') && formErrors['employeeId'] ? 'border-red-500 bg-red-50' : ''
                    } ${editingLetterId ? 'bg-gray-100 cursor-not-allowed opacity-75' : 'bg-white'}`}
                  title={selectedEmployeeLabel}
                  aria-haspopup="listbox"
                  aria-expanded={isEmployeeDropdownOpen}
                >
                  <span className="block truncate">{selectedEmployeeLabel}</span>
                </button>

                {isEmployeeDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
                        <input
                          type="text"
                          value={employeeSearchTerm}
                          onChange={(e) => {
                            const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                            setEmployeeSearchTerm(value);
                          }}
                          placeholder="Search employees..."
                          className="input-field pl-10"
                          autoFocus
                        />
                      </div>
                    </div>
                    <ul role="listbox" className="max-h-64 overflow-auto py-1">
                      {isLoadingEmployees ? (
                        <li className="px-3 py-2 text-sm text-gray-500 flex items-center">
                          <RefreshCw className="h-3 w-3 animate-spin mr-2" />
                          Loading...
                        </li>
                      ) : employees.length > 0 ? (
                        employees.map((employee) => {
                          const value = employee.employeeId || employee.id || employee._id;
                          const name = employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.employeeName;
                          const email = employee.email || employee.personalEmail || employee.workEmail || '';
                          const dept = employee.department || employee.employmentInfo?.department || '';
                          const selected = value === letterForm.employeeId;
                          return (
                            <li
                              key={employee._id || value}
                              role="option"
                              aria-selected={selected}
                              className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selected ? 'bg-blue-50' : ''}`}
                              onClick={async () => {
                                // Find the employee and their exit request data
                                const selectedEmployee = employeesData?.find((emp) => {
                                  const empValue = emp.employeeId || emp.id || emp._id;
                                  return empValue === value;
                                });

                                if (selectedEmployee?.exitRequestData) {
                                  const matchingRequest = selectedEmployee.exitRequestData;
                                  const emp = matchingRequest.employeeId;

                                  const joinDate = emp?.employmentInfo?.joinDate
                                    ? new Date(emp.employmentInfo.joinDate).toISOString().split('T')[0]
                                    : '';
                                  const lastWorkingDate = matchingRequest.lastWorkingDate
                                    ? new Date(matchingRequest.lastWorkingDate).toISOString().split('T')[0]
                                    : '';

                                  // Fetch reporting manager details if requested
                                  let reportingManagerObj = { employeeId: '', name: '', email: '', role: '', department: '' };
                                  let reportingManagerDesignation = '';

                                  if (emp?.employmentInfo?.reportingManager) {
                                    const managerRaw = emp.employmentInfo.reportingManager;
                                    if (typeof managerRaw === 'object' && (managerRaw.personalInfo?.firstName || managerRaw.firstName)) {
                                      const mgrFirstName = managerRaw.personalInfo?.firstName || managerRaw.firstName || '';
                                      const mgrLastName = managerRaw.personalInfo?.lastName || managerRaw.lastName || '';
                                      reportingManagerDesignation = managerRaw.employmentInfo?.designation || managerRaw.position || '';
                                      reportingManagerObj = {
                                        employeeId: managerRaw.employeeId || managerRaw._id || managerRaw.id,
                                        id: managerRaw._id || managerRaw.id,
                                        name: `${mgrFirstName} ${mgrLastName}`.trim(),
                                        email: managerRaw.contactInfo?.personalEmail || managerRaw.email || '',
                                        role: reportingManagerDesignation,
                                        department: managerRaw.employmentInfo?.department || managerRaw.department || ''
                                      };
                                    } else {
                                      try {
                                        const managerId = typeof managerRaw === 'object' ? (managerRaw._id || managerRaw.id) : managerRaw;
                                        const authToken = (typeof window !== 'undefined' && localStorage.getItem('auth_token')) || null;
                                        const headers = { 'Content-Type': 'application/json', ...(authToken && { Authorization: `Bearer ${authToken}` }) };
                                        const mRes = await fetch(`${API_BASE_URL}/hr/employees/${managerId}`, { method: 'GET', headers });
                                        if (mRes.ok) {
                                          const mData = await mRes.json();
                                          const mgr = mData.data || mData;
                                          const mgrFirstName = mgr.personalInfo?.firstName || mgr.firstName || '';
                                          const mgrLastName = mgr.personalInfo?.lastName || mgr.lastName || '';
                                          reportingManagerDesignation = mgr.employmentInfo?.designation || mgr.position || '';
                                          reportingManagerObj = {
                                            employeeId: mgr.employeeId || mgr._id || mgr.id || managerId,
                                            id: mgr._id || mgr.id || managerId,
                                            name: `${mgrFirstName} ${mgrLastName}`.trim(),
                                            email: mgr.contactInfo?.personalEmail || mgr.email || '',
                                            role: reportingManagerDesignation,
                                            department: mgr.employmentInfo?.department || mgr.department || ''
                                          };
                                        }
                                      } catch (err) {
                                      }
                                    }
                                  }

                                  // Get net salary (fetch full employee for accuracy)
                                  let netSalary = emp?.salaryInfo?.netSalary || emp?.salaryInfo?.salaryStructure?.netPay || 0;
                                  try {
                                    const authToken = (typeof window !== 'undefined' && localStorage.getItem('auth_token')) || null;
                                    const headers = { 'Content-Type': 'application/json', ...(authToken && { Authorization: `Bearer ${authToken}` }) };
                                    const eRes = await fetch(`${API_BASE_URL}/hr/employees/${emp._id || emp.id || value}`, { method: 'GET', headers });
                                    if (eRes.ok) {
                                      const eData = await eRes.json();
                                      const fullEmp = eData.data || eData;
                                      if (fullEmp?.salaryInfo?.netSalary) {
                                        netSalary = fullEmp.salaryInfo.netSalary;
                                      } else if (fullEmp?.salaryInfo?.salaryStructure?.netPay) {
                                        netSalary = fullEmp.salaryInfo.salaryStructure.netPay;
                                      }
                                    }
                                  } catch (err) {
                                  }

                                  // Calculate notice period served
                                  let noticeServedDays = 0;
                                  let resignationDate = matchingRequest.resignationDate || matchingRequest.submittedDate || matchingRequest.createdAt || '';
                                  if (resignationDate) {
                                    resignationDate = new Date(resignationDate).toISOString().split('T')[0];
                                    if (lastWorkingDate) {
                                      const res = new Date(resignationDate);
                                      const lwd = new Date(lastWorkingDate);
                                      noticeServedDays = Math.max(0, Math.ceil((lwd - res) / (1000 * 60 * 60 * 24)));
                                    }
                                  }

                                  // No dues status
                                  const matchingNoDues = noDuesClearances.find(nd => String(nd.employeeDbId || nd.employeeId?._id || nd.employee?._id || nd.employeeId || '') === String(value) || String(nd.employeeId?.employeeId || nd.employeeCode || '') === String(emp?.employeeId || ''));
                                  const noDuesStatus = matchingNoDues && matchingNoDues.clearanceStatus === 'completed' ? 'approved' : 'pending';

                                  setLetterForm({
                                    ...letterForm,
                                    employeeId: value,
                                    employeeName: name,
                                    employeeIdNumber: employee.employeeId || employee._id || employee.id,
                                    exitRequestId: matchingRequest._id || matchingRequest.id,
                                    noDuesClearanceId: matchingNoDues ? (matchingNoDues._id || matchingNoDues.id) : '',
                                    noDuesStatus: noDuesStatus,
                                    resignationDate: resignationDate,
                                    noticeServedDays: noticeServedDays,
                                    noticePeriodDays: matchingRequest.noticePeriod || 30,
                                    acceptanceDate: matchingRequest.approvedDate ? new Date(matchingRequest.approvedDate).toISOString().split('T')[0] : (matchingRequest.hrApproval?.timestamp ? new Date(matchingRequest.hrApproval.timestamp).toISOString().split('T')[0] : (matchingRequest.managerApproval?.timestamp ? new Date(matchingRequest.managerApproval.timestamp).toISOString().split('T')[0] : '')),
                                    exitType: matchingRequest.resignationType === 'termination' ? 'termination' : (matchingRequest.resignationCategory === 'Retirement' ? 'retirement' : 'voluntary'),
                                    authorization: {
                                      ...letterForm.authorization,
                                      signatoryId: reportingManagerObj.id,
                                      name: reportingManagerObj.name,
                                      email: reportingManagerObj.email,
                                      role: reportingManagerObj.role,
                                      department: reportingManagerObj.department
                                    },
                                    employmentDetails: {
                                      ...letterForm.employmentDetails,
                                      joinDate: joinDate,
                                      lastWorkingDate: lastWorkingDate,
                                      employmentType: emp?.employmentInfo?.employmentType || 'full-time',
                                      workLocation: (() => { const l = emp?.workLocation?.fromLocation || emp?.workLocation || emp?.location || emp?.employmentInfo?.workLocation; return typeof l === 'object' && l ? (l.name || l.label || l.value || '') : (l || ''); })(),
                                      reportingManager: reportingManagerObj,
                                      reasonForLeaving: matchingRequest.resignationType || 'resignation',
                                      reasonDescription: matchingRequest.detailedReason || matchingRequest.reason || ''
                                    },
                                    financialDetails: {
                                      ...letterForm.financialDetails,
                                      lastSalary: netSalary
                                    },
                                    letterContent: {
                                      ...letterForm.letterContent,
                                      signature: reportingManagerObj.name,
                                      designation: reportingManagerDesignation
                                    }
                                  });
                                  setSelectedSignatory(reportingManagerObj);
                                } else {
                                  // Fetch no dues status even if no exit request is found
                                  const matchingNoDues = noDuesClearances.find(nd => String(nd.employeeDbId || nd.employeeId?._id || nd.employee?._id || nd.employeeId || '') === String(value));
                                  const noDuesStatus = matchingNoDues && matchingNoDues.clearanceStatus === 'completed' ? 'approved' : 'pending';

                                  // If no exit request data found, set employee ID and noDues status
                                  setLetterForm({
                                    ...letterForm,
                                    employeeId: value,
                                    employeeName: name,
                                    employeeIdNumber: employee.employeeId || employee._id || employee.id,
                                    noDuesClearanceId: matchingNoDues ? (matchingNoDues._id || matchingNoDues.id) : '',
                                    noDuesStatus: noDuesStatus
                                  });
                                }

                                setFormErrors((prev) => ({
                                  ...prev,
                                  employeeId: validateField('employeeId', value),
                                  'authorization.signatoryId': undefined
                                }));
                                setIsEmployeeDropdownOpen(false);
                                setEmployeeSearchTerm('');
                              }}
                            >
                              <div className="text-sm text-gray-900">{name}{dept ? ` - ${dept}` : ''}</div>
                              {email && <div className="text-xs text-gray-500">{email}</div>}
                            </li>
                          );
                        })
                      ) : (
                        <li className="px-3 py-2 text-sm text-gray-500">No employees found</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              {letterTouchedFields.has('employeeId') && formErrors['employeeId'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['employeeId']}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Join Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={letterForm.employmentDetails.joinDate}
                onChange={(e) => {
                  setLetterForm({
                    ...letterForm,
                    employmentDetails: { ...letterForm.employmentDetails, joinDate: e.target.value }
                  });
                  setFormErrors(prev => ({ ...prev, 'employmentDetails.joinDate': validateField('employmentDetails.joinDate', e.target.value) }));
                }}
                onBlur={() => {
                  handleLetterFieldBlur('employmentDetails.joinDate');
                  setFormErrors(prev => ({ ...prev, 'employmentDetails.joinDate': validateField('employmentDetails.joinDate', letterForm.employmentDetails.joinDate) }));
                }}
                onClick={(e) => !letterForm.exitRequestId && e.target.showPicker?.()}
                onFocus={(e) => !letterForm.exitRequestId && e.target.showPicker?.()}
                readOnly={!!letterForm.exitRequestId}
                className={`input-field ${letterTouchedFields.has('employmentDetails.joinDate') && formErrors['employmentDetails.joinDate'] ? 'border-red-500 bg-red-50' : ''} ${letterForm.exitRequestId ? 'bg-gray-50' : 'bg-white'}`}
              />
              {letterTouchedFields.has('employmentDetails.joinDate') && formErrors['employmentDetails.joinDate'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['employmentDetails.joinDate']}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Working Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={letterForm.employmentDetails.lastWorkingDate}
                onChange={(e) => {
                  setLetterForm({
                    ...letterForm,
                    employmentDetails: { ...letterForm.employmentDetails, lastWorkingDate: e.target.value }
                  });
                  setFormErrors(prev => ({ ...prev, 'employmentDetails.lastWorkingDate': validateField('employmentDetails.lastWorkingDate', e.target.value) }));
                }}
                onBlur={() => {
                  handleLetterFieldBlur('employmentDetails.lastWorkingDate');
                  setFormErrors(prev => ({ ...prev, 'employmentDetails.lastWorkingDate': validateField('employmentDetails.lastWorkingDate', letterForm.employmentDetails.lastWorkingDate) }));
                }}
                onClick={(e) => !letterForm.exitRequestId && e.target.showPicker?.()}
                onFocus={(e) => !letterForm.exitRequestId && e.target.showPicker?.()}
                readOnly={!!letterForm.exitRequestId}
                className={`input-field ${letterTouchedFields.has('employmentDetails.lastWorkingDate') && formErrors['employmentDetails.lastWorkingDate'] ? 'border-red-500 bg-red-50' : ''} ${letterForm.exitRequestId ? 'bg-gray-50' : 'bg-white'}`}
              />
              {letterTouchedFields.has('employmentDetails.lastWorkingDate') && formErrors['employmentDetails.lastWorkingDate'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['employmentDetails.lastWorkingDate']}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employment Type <span className="text-red-500">*</span>
              </label>
              <select
                value={letterForm.employmentDetails.employmentType}
                onChange={(e) => {
                  setLetterForm({
                    ...letterForm,
                    employmentDetails: { ...letterForm.employmentDetails, employmentType: e.target.value }
                  });
                  setFormErrors(prev => ({ ...prev, 'employmentDetails.employmentType': validateField('employmentDetails.employmentType', e.target.value) }));
                }}
                onBlur={() => {
                  handleLetterFieldBlur('employmentDetails.employmentType');
                  setFormErrors(prev => ({ ...prev, 'employmentDetails.employmentType': validateField('employmentDetails.employmentType', letterForm.employmentDetails.employmentType) }));
                }}
                className={`input-field ${letterTouchedFields.has('employmentDetails.employmentType') && formErrors['employmentDetails.employmentType'] ? 'border-red-500 bg-red-50' : ''}`}
              >
                <option value="full-time">Full Time</option>
                <option value="part-time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="intern">Intern</option>
                <option value="consultant">Consultant</option>
              </select>
              {letterTouchedFields.has('employmentDetails.employmentType') && formErrors['employmentDetails.employmentType'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['employmentDetails.employmentType']}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Work Location <span className="text-red-500">*</span>
              </label>
              <select
                value={letterForm.employmentDetails.workLocation}
                onChange={(e) => {
                  setLetterForm({
                    ...letterForm,
                    employmentDetails: { ...letterForm.employmentDetails, workLocation: e.target.value }
                  });
                  validateField('employmentDetails.workLocation', e.target.value);
                }}
                onBlur={() => {
                  handleLetterFieldBlur('employmentDetails.workLocation');
                  setFormErrors(prev => ({ ...prev, 'employmentDetails.workLocation': validateField('employmentDetails.workLocation', letterForm.employmentDetails.workLocation) }));
                }}
                required
                className={`input-field ${letterTouchedFields.has('employmentDetails.workLocation') && formErrors['employmentDetails.workLocation'] ? 'border-red-500 bg-red-50' : ''}`}
              >
                <option value="">Select Work Location</option>
                {isLoadingLocations ? (
                  <option disabled>Loading locations...</option>
                ) : locations.length > 0 ? (
                  locations.map((location) => (
                    <option key={location._id || location.id} value={location.name || location.label || location.value}>
                      {location.name || location.label || location.value}
                    </option>
                  ))
                ) : (
                  <option disabled>No locations available</option>
                )}
              </select>
              {letterTouchedFields.has('employmentDetails.workLocation') && formErrors['employmentDetails.workLocation'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['employmentDetails.workLocation']}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Manager</label>
              <div className="relative" ref={reportingManagerDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsReportingManagerDropdownOpen((o) => !o);
                    if (!isReportingManagerDropdownOpen) {
                      setReportingManagerSearchTerm('');
                    }
                  }}
                  className="input-field text-left"
                  aria-haspopup="listbox"
                  aria-expanded={isReportingManagerDropdownOpen}
                >
                  {(() => {
                    // Try to find selected employee in current list or stored value
                    const selected = reportingManagerEmployees.find((e) => {
                      const firstName = e.personalInfo?.firstName || e.firstName || '';
                      const lastName = e.personalInfo?.lastName || e.lastName || '';
                      const name = e.fullName || `${firstName} ${lastName}`.trim() || e.employeeName;
                      const employeeId = e.employeeId || e.id || e._id;
                      const rmValue = letterForm.employmentDetails.reportingManager?.employeeId || letterForm.employmentDetails.reportingManager?.name || letterForm.employmentDetails.reportingManager;
                      return name === rmValue || employeeId === rmValue;
                    });
                    if (selected) {
                      const firstName = selected.personalInfo?.firstName || selected.firstName || '';
                      const lastName = selected.personalInfo?.lastName || selected.lastName || '';
                      const name = selected.fullName || `${firstName} ${lastName}`.trim() || selected.employeeName;
                      const employeeId = selected.employeeId || selected.id || selected._id;
                      return `${name} (${employeeId})`;
                    }
                    // If not found in list but we have a stored value, show it
                    if (letterForm.employmentDetails.reportingManager) {
                      return typeof letterForm.employmentDetails.reportingManager === 'object' ? (letterForm.employmentDetails.reportingManager.name || 'Select Reporting Manager') : letterForm.employmentDetails.reportingManager;
                    }
                    return 'Select Reporting Manager';
                  })()}
                </button>

                {isReportingManagerDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
                        <input
                          type="text"
                          value={reportingManagerSearchTerm}
                          onChange={(e) => {
                            const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                            setReportingManagerSearchTerm(value);
                          }}
                          placeholder="Search employees..."
                          className="input-field pl-10"
                          autoFocus
                        />
                      </div>
                    </div>
                    <ul role="listbox" className="max-h-64 overflow-auto py-1">
                      {isLoadingReportingManager ? (
                        <li className="px-3 py-2 text-sm text-gray-500 flex items-center">
                          <RefreshCw className="h-3 w-3 animate-spin mr-2" />
                          {debouncedReportingManagerSearch && debouncedReportingManagerSearch.trim() ? 'Searching...' : 'Loading...'}
                        </li>
                      ) : reportingManagerEmployees.length > 0 ? (
                        reportingManagerEmployees.map((employee) => {
                          const firstName = employee.personalInfo?.firstName || employee.firstName || '';
                          const lastName = employee.personalInfo?.lastName || employee.lastName || '';
                          const name = employee.fullName || `${firstName} ${lastName}`.trim() || employee.employeeName;
                          const employeeId = employee.employeeId || employee.id || employee._id;
                          const dept = employee.employmentInfo?.department || employee.department || '';
                          const rmValue = letterForm.employmentDetails.reportingManager?.employeeId || letterForm.employmentDetails.reportingManager?.name || letterForm.employmentDetails.reportingManager;
                          const selected = name === rmValue || employeeId === rmValue;
                          return (
                            <li
                              key={employee._id || employeeId}
                              role="option"
                              aria-selected={selected}
                              className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selected ? 'bg-blue-50' : ''}`}
                              onClick={() => {
                                setLetterForm({
                                  ...letterForm,
                                  employmentDetails: {
                                    ...letterForm.employmentDetails, reportingManager: {
                                      employeeId: employeeId,
                                      name: name,
                                      email: employee.contactInfo?.personalEmail || employee.email || '',
                                      role: employee.employmentInfo?.designation || employee.position || '',
                                      department: dept
                                    }
                                  }
                                });
                                setIsReportingManagerDropdownOpen(false);
                                setReportingManagerSearchTerm('');
                              }}
                            >
                              <div className="text-sm font-medium text-gray-900">
                                {firstName} {lastName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {employeeId} - {dept}
                              </div>
                            </li>
                          );
                        })
                      ) : (
                        <li className="px-3 py-2 text-sm text-gray-500">
                          {debouncedReportingManagerSearch && debouncedReportingManagerSearch.trim()
                            ? 'No employees found'
                            : 'Type to search employees...'}
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {isReportingManagerDropdownOpen && debouncedReportingManagerSearch && debouncedReportingManagerSearch.trim()
                  ? `Found ${reportingManagerEmployees.length} employee(s)`
                  : isReportingManagerDropdownOpen
                    ? reportingManagerEmployees.length > 0
                      ? `Showing ${reportingManagerEmployees.length} employees. Type to search...`
                      : 'Type to search employees...'
                    : ''}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Leaving <span className="text-red-500">*</span>
              </label>
              <select
                value={letterForm.employmentDetails.reasonForLeaving}
                onChange={(e) => {
                  setLetterForm({
                    ...letterForm,
                    employmentDetails: { ...letterForm.employmentDetails, reasonForLeaving: e.target.value }
                  });
                  validateField('employmentDetails.reasonForLeaving', e.target.value);
                }}
                onBlur={() => {
                  handleLetterFieldBlur('employmentDetails.reasonForLeaving');
                  setFormErrors(prev => ({ ...prev, 'employmentDetails.reasonForLeaving': validateField('employmentDetails.reasonForLeaving', letterForm.employmentDetails.reasonForLeaving) }));
                }}
                required
                className={`input-field ${letterTouchedFields.has('employmentDetails.reasonForLeaving') && formErrors['employmentDetails.reasonForLeaving'] ? 'border-red-500 bg-red-50' : ''}`}
              >
                <option value="resignation">Resignation</option>
                <option value="voluntary">Voluntary Resignation</option>
                <option value="termination">Termination</option>
                <option value="involuntary">Involuntary Termination</option>
                <option value="retirement">Retirement</option>
                <option value="contract_end">Contract End</option>
                <option value="layoff">Layoff</option>
                <option value="other">Other</option>
              </select>
              {letterTouchedFields.has('employmentDetails.reasonForLeaving') && formErrors['employmentDetails.reasonForLeaving'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['employmentDetails.reasonForLeaving']}
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={letterForm.employmentDetails.reasonDescription}
                maxLength={3000}
                onChange={(e) => {
                  let value = e.target.value;
                  // Filter out invalid characters
                  value = value.replace(INVALID_CHARS_REGEX, '');
                  // Enforce max length
                  if (value.length > 500) {
                    value = value.substring(0, 500);
                  }
                  setLetterForm({
                    ...letterForm,
                    employmentDetails: { ...letterForm.employmentDetails, reasonDescription: value }
                  });
                  validateField('employmentDetails.reasonDescription', value);
                }}
                onBlur={() => {
                  handleLetterFieldBlur('employmentDetails.reasonDescription');
                  setFormErrors(prev => ({ ...prev, 'employmentDetails.reasonDescription': validateField('employmentDetails.reasonDescription', letterForm.employmentDetails.reasonDescription) }));
                }}
                placeholder="Enter detailed reason for leaving"
                rows={6}
                className={`input-field ${letterTouchedFields.has('employmentDetails.reasonDescription') && formErrors['employmentDetails.reasonDescription'] ? 'border-red-500 bg-red-50' : ''}`}
              />
              <div className="flex justify-between items-start mt-1">
                <div className="flex-1">
                  {letterTouchedFields.has('employmentDetails.reasonDescription') && formErrors['employmentDetails.reasonDescription'] && (
                    <p className="text-xs text-red-600 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {formErrors['employmentDetails.reasonDescription']}
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 ml-auto whitespace-nowrap">
                  {letterForm.employmentDetails?.reasonDescription?.length || 0} / 3000 characters
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Legal & Notice Tracking */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 border-b pb-2">Legal & Notice Tracking</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Resignation Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={letterForm.resignationDate}
                onChange={(e) => {
                  setLetterForm({ ...letterForm, resignationDate: e.target.value });
                  setFormErrors(prev => ({ ...prev, resignationDate: validateField('resignationDate', e.target.value) }));
                }}
                onBlur={() => {
                  handleLetterFieldBlur('resignationDate');
                  setFormErrors(prev => ({ ...prev, resignationDate: validateField('resignationDate', letterForm.resignationDate) }));
                }}
                readOnly={!!letterForm.exitRequestId}
                className={`input-field ${letterTouchedFields.has('resignationDate') && formErrors['resignationDate'] ? 'border-red-500 bg-red-50' : ''} ${letterForm.exitRequestId ? 'bg-gray-50' : 'bg-white'}`}
              />
              {letterTouchedFields.has('resignationDate') && formErrors['resignationDate'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['resignationDate']}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Acceptance Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={letterForm.acceptanceDate}
                onChange={(e) => {
                  setLetterForm({ ...letterForm, acceptanceDate: e.target.value });
                  setFormErrors(prev => ({ ...prev, acceptanceDate: validateField('acceptanceDate', e.target.value) }));
                }}
                onBlur={() => {
                  handleLetterFieldBlur('acceptanceDate');
                  setFormErrors(prev => ({ ...prev, acceptanceDate: validateField('acceptanceDate', letterForm.acceptanceDate) }));
                }}
                className={`input-field ${letterTouchedFields.has('acceptanceDate') && formErrors['acceptanceDate'] ? 'border-red-500 bg-red-50' : ''}`}
              />
              {letterTouchedFields.has('acceptanceDate') && formErrors['acceptanceDate'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['acceptanceDate']}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Exit Type</label>
              <select
                value={letterForm.exitType}
                onChange={(e) => setLetterForm({ ...letterForm, exitType: e.target.value })}
                className="input-field"
              >
                <option value="voluntary">Voluntary Resignation</option>
                <option value="termination">Termination</option>
                <option value="absconding">Absconding</option>
                <option value="retirement">Retirement</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notice Period (Days)</label>
              <input
                type="number"
                value={letterForm.noticePeriodDays}
                onChange={(e) => setLetterForm({ ...letterForm, noticePeriodDays: parseInt(e.target.value) || 0 })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notice Served (Days)</label>
              <input
                type="number"
                value={letterForm.noticeServedDays}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notice Shortfall (Days)</label>
              <input
                type="number"
                value={Math.max(0, letterForm.noticePeriodDays - letterForm.noticeServedDays)}
                className="input-field"
              />
            </div>
          </div>
        </div>
        {/* Performance Details */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 border-b pb-2">Performance Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overall Rating <span className="text-red-500">*</span>
              </label>
              <select
                value={letterForm.performanceDetails.overallRating}
                onChange={(e) => {
                  setLetterForm({
                    ...letterForm,
                    performanceDetails: { ...letterForm.performanceDetails, overallRating: e.target.value }
                  });
                  setFormErrors(prev => ({ ...prev, 'performanceDetails.overallRating': validateField('performanceDetails.overallRating', e.target.value) }));
                }}
                onBlur={() => {
                  handleLetterFieldBlur('performanceDetails.overallRating');
                  setFormErrors(prev => ({ ...prev, 'performanceDetails.overallRating': validateField('performanceDetails.overallRating', letterForm.performanceDetails.overallRating) }));
                }}
                required
                className={`input-field ${letterTouchedFields.has('performanceDetails.overallRating') && formErrors['performanceDetails.overallRating'] ? 'border-red-500 bg-red-50' : ''}`}
              >
                <option value="excellent">Excellent</option>
                <option value="very_good">Very Good</option>
                <option value="good">Good</option>
                <option value="satisfactory">Satisfactory</option>
                <option value="needs_improvement">Needs Improvement</option>
              </select>
              {letterTouchedFields.has('performanceDetails.overallRating') && formErrors['performanceDetails.overallRating'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['performanceDetails.overallRating']}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Skills (comma separated)</label>
              <input
                type="text"
                value={letterForm.performanceDetails.skills}
                maxLength={200}
                onChange={(e) => {
                  let value = e.target.value;
                  // Filter out invalid characters
                  value = value.replace(INVALID_CHARS_REGEX, '');

                  setLetterForm({
                    ...letterForm,
                    performanceDetails: { ...letterForm.performanceDetails, skills: value }
                  });
                }}
                placeholder="JavaScript, React, Node.js, C++, ASP.NET"
                className="input-field"
              />
              <p className="mt-1 text-xs text-gray-500">
                {letterForm.performanceDetails?.skills?.length || 0} / 200 characters
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Performance Notes</label>
              <textarea
                value={letterForm.performanceDetails.performanceNotes}
                maxLength={3000}
                onChange={(e) => {
                  let value = e.target.value;
                  // Filter out invalid characters
                  value = value.replace(INVALID_CHARS_REGEX, '');
                  // Enforce max length
                  if (value.length > 1000) {
                    value = value.substring(0, 1000);
                  }
                  setLetterForm({
                    ...letterForm,
                    performanceDetails: { ...letterForm.performanceDetails, performanceNotes: value }
                  });
                }}
                onBlur={() => handleLetterFieldBlur('performanceDetails.performanceNotes')}
                placeholder="Enter performance notes and achievements"
                rows={6}
                className="input-field"
              />
              <div className="flex justify-end mt-1">
                <p className="text-xs text-gray-500">
                  {letterForm.performanceDetails?.performanceNotes?.length || 0} / 3000 characters
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Details */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 border-b pb-2">Financial Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Net Salary <span className="text-red-500">*</span></label>
              <input
                type="number"
                value={letterForm.financialDetails.lastSalary}
                readOnly
                className={`input-field bg-gray-50 ${letterTouchedFields.has('financialDetails.lastSalary') && formErrors['financialDetails.lastSalary'] ? 'border-red-500 bg-red-50' : ''}`}
              />
              {letterTouchedFields.has('financialDetails.lastSalary') && formErrors['financialDetails.lastSalary'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['financialDetails.lastSalary']}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 border-b pb-2">Compliance & Control</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">No-Dues Status</label>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded text-xs font-semibold ${letterForm.noDuesStatus === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {letterForm.noDuesStatus.toUpperCase()}
                </span>
                <span className="text-xs text-gray-500">(Auto-synced from No-Dues module)</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">FNF Settlement Status</label>
              <select
                value={letterForm.fnfStatus}
                onChange={(e) => setLetterForm({ ...letterForm, fnfStatus: e.target.value })}
                className="input-field"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="settled">Settled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Authorization */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 border-b pb-2">Authorization</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative" ref={signatoryDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Authorized Signatory <span className="text-red-500">*</span></label>
              <div
                onClick={() => setIsSignatoryDropdownOpen(!isSignatoryDropdownOpen)}
                className={`input-field cursor-pointer flex justify-between items-center ${isSignatoryDropdownOpen ? 'ring-1 ring-brand border-brand' : ''} bg-white`}
              >
                <span className={letterForm.letterContent.signature ? 'text-gray-900' : 'text-gray-400'}>
                  {letterForm.letterContent.signature || "Select HR Manager / Admin"}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isSignatoryDropdownOpen ? 'rotate-180' : ''}`} />
              </div>
              {letterTouchedFields.has('authorization.signatoryId') && formErrors['authorization.signatoryId'] && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {formErrors['authorization.signatoryId']}
                </p>
              )}

              {isSignatoryDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                  <div className="p-2 border-b bg-gray-50">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search signatory..."
                        value={signatorySearchTerm}
                        onChange={(e) => setSignatorySearchTerm(e.target.value)}
                        className="input-field pl-8 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <ul className="max-h-60 overflow-auto py-1">
                    {isLoadingSignatories ? (
                      <li className="px-3 py-4 text-center text-sm text-gray-500 flex flex-col items-center">
                        <RefreshCw className="h-5 w-5 animate-spin mb-2 text-blue-500" />
                        Finding authorized personnel...
                      </li>
                    ) : signatories.length === 0 ? (
                      <li className="px-3 py-4 text-center text-sm text-gray-500">
                        {signatorySearchTerm ? 'No matching signatories found' : 'No HR Managers or Admins found'}
                      </li>
                    ) : (
                      signatories.map((sig) => (
                        <li
                          key={sig._id || sig.id}
                          onClick={() => {
                            const sigName = resolveEmployeeName(sig);
                            setLetterForm({
                              ...letterForm,
                              authorization: {
                                ...letterForm.authorization,
                                signatoryId: sig._id || sig.id,
                                name: sigName,
                                email: sig.contactInfo?.personalEmail || sig.email || sig.workEmail || '',
                                role: sig.designation || sig.employmentInfo?.designation || 'Signatory',
                                department: sig.department || sig.employmentInfo?.department || ''
                              },
                              letterContent: {
                                ...letterForm.letterContent,
                                signature: sigName,
                                designation: sig.designation || sig.employmentInfo?.designation || 'Signatory'
                              }
                            });
                            setSelectedSignatory(sig);
                            setIsSignatoryDropdownOpen(false);
                            setSignatorySearchTerm('');
                          }}
                          className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0"
                        >
                          <div className="flex justify-between items-start">
                            <div className="font-semibold text-sm text-gray-900">{resolveEmployeeName(sig)}</div>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-[#0B2033] font-bold uppercase">
                              {sig.role || sig.userRole || 'Staff'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-y-1">
                            <span className="bg-gray-100 px-1 rounded mr-2 font-mono">{sig.employeeId || sig.id || 'N/A'}</span>
                            <span className={`px-1 rounded mr-2 font-medium ${sig.department?.toLowerCase() === 'administration' || sig.employmentInfo?.department?.toLowerCase() === 'administration' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-blue-50 text-[#0B2033]'}`}>
                              {sig.department || sig.employmentInfo?.department || 'No Department'}
                            </span>
                            <span className="truncate text-gray-400 italic">{sig.contactInfo?.workEmail || sig.email || 'No email'}</span>
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
              <input
                type="text"
                value={letterForm.letterContent.designation}
                onChange={(e) => setLetterForm({
                  ...letterForm,
                  letterContent: { ...letterForm.letterContent, designation: e.target.value }
                })}
                placeholder="Enter Signatory Designation"
                className="input-field"
              />
            </div>

            {selectedSignatory && (
              <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-md animate-fadeIn w-full max-w-sm relative">
                <button
                  type="button"
                  onClick={() => setSelectedSignatory(null)}
                  className="absolute top-2 right-2 p-1 hover:bg-white rounded border border-transparent hover:border-blue-200 text-gray-400 hover:text-red-500 transition-all"
                  title="Clear selection"
                >
                  <X className="h-3.5 w-3.5" />
                </button>

                <div className="flex flex-col space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-[#0F2942]">
                      <User className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-400 font-bold uppercase tracking-tight leading-none mb-0.5">Authorized Personnel</div>
                      <div className="text-sm font-bold text-gray-800 leading-tight">{resolveEmployeeName(selectedSignatory)}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-blue-100">
                    <div className="flex flex-col">
                      <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">Role</span>
                      <span className="text-xs font-bold text-[#0B2033] uppercase">{selectedSignatory.role || selectedSignatory.userRole || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="flex flex-col pt-1">
                    <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">Department</span>
                    <span className="text-xs text-gray-600">{selectedSignatory.department || selectedSignatory.employmentInfo?.department || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => {
              setShowLetterForm(false);
              resetLetterForm();
            }}
            className="btn-cancel"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handlePreviewLetter}
            className="btn-create flex items-center"
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview Letter
          </button>


        </div>
      </form >
    </div >
  );

  // Render letters list
  const renderLettersList = () => {
    return (
      <div className="space-y-6">
        {/* KPI Cards - Moved from Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(() => {
            const KpiCard = ({ icon: Icon, title, value, color }) => {
              let bgColor = "bg-[#0F2942]";
              if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
              else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
              else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
              else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
              else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

              return (
                <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
                  <div className="space-y-4 flex-1">
                    <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
                      {title}
                    </h3>
                    <div className="flex items-baseline space-x-2">
                      <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
                        {value}
                      </span>
                    </div>
                  </div>
                  <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
              );
            };

            return (
              <>
                <KpiCard
                  icon={FileText}
                  title="Total Letters"
                  value={formatIndianNumber(letterStats.totalLetters || 0)}
                  color="bg-[#0F2942]"
                />
                <KpiCard
                  icon={Clock}
                  title="Pending Letters"
                  value={formatIndianNumber(letterStats.pendingLetters || 0)}
                  color="bg-amber-600"
                />
                <KpiCard
                  icon={Send}
                  title="Sent Letters"
                  value={formatIndianNumber(letterStats.sentLetters || 0)}
                  color="bg-emerald-600"
                />
              </>
            );
          })()}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-end gap-4 w-full">
            {/* Search Bar */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and approved symbols
                  const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                  setSearchTerm(value);
                  // Page reset is handled by debounce effect
                }}
                placeholder="Search by Employee , Letter ID or Email ID..."
                maxLength={100}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    // Maintain focus on input
                    const input = document.activeElement;
                    if (input && input.tagName === 'INPUT') {
                      setTimeout(() => input.focus(), 0);
                    }
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div className="flex-shrink-0">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); }}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
              </select>
            </div>

            {/* Department Filter */}
            <div className="flex-shrink-0 w-48">
              <select
                value={departmentFilter}
                onChange={(e) => { setDepartmentFilter(e.target.value); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60 truncate"
              >
                <option value="all">All Departments</option>
                {departments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            {/* Date Range Filter — filters by Last Working Date */}
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onChange={setDateRange}
                containerClassName="flex items-center space-x-2"
                allowFutureDates={true}
                inputClassName="text-sm w-36 cursor-pointer px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
              />
            </div>

            {/* Clear Filters Button */}
            {(searchTerm || statusFilter !== 'all' || departmentFilter !== 'all' || startDate || endDate) && (
              <div className="relative flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => {
                    resetLetterFilters();
                    setSortConfig({ key: null, direction: 'asc' });
                  }}
                  className="flex items-center space-x-2"
                >
                  <X className="h-4 w-4" />
                  <span>Clear</span>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Letters Table + Pagination */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">All Letters</h3>
          </div>

          {isLoadingLetters ? (
            <div className="flex justify-center items-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          ) : relievingLetters.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No letters found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={relievingLetters.length > 0 && selectedRows.size === relievingLetters.length}
                          className="rounded border-gray-300 focus:ring-brand text-brand"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('letterId')}>
                        <div className="flex items-center space-x-1">
                          <span>Letter ID</span>
                          {sortConfig.key === 'letterId' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('employeeName')}>
                        <div className="flex items-center space-x-1">
                          <span>Employee</span>
                          {sortConfig.key === 'employeeName' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('department')}>
                        <div className="flex items-center space-x-1">
                          <span>Department</span>
                          {sortConfig.key === 'department' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('lastWorkingDate')}>
                        <div className="flex items-center space-x-1">
                          <span>Last Working Date</span>
                          {sortConfig.key === 'lastWorkingDate' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {sortConfig.key === 'status' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('createdAt')}>
                        <div className="flex items-center space-x-1">
                          <span>Created At</span>
                          {sortConfig.key === 'createdAt' ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedRelievingLetters.map((letter) => (
                      <tr key={letter._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(letter._id)}
                            onChange={() => handleSelectRow(letter._id)}
                            className="rounded border-gray-300 focus:ring-brand text-brand"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {letter.letterId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap max-w-xs">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <User className="h-5 w-5 text-gray-600" />
                              </div>
                            </div>
                            <div className="ml-4 min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">{letter.employeeName}</div>
                              <div className="text-sm text-gray-500 truncate">{letter.employeeEmail}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap max-w-xs">
                          <div className="text-sm text-gray-900 truncate capitalize">{letter.department}</div>
                          <div className="text-sm text-gray-500 truncate capitalize">{letter.employeeId?.employmentInfo?.designation || letter.position || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(letter.employmentDetails?.lastWorkingDate, { includeTime: false })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(letter.status)}`}>
                            {getStatusIcon(letter.status)}
                            <span className="ml-1 capitalize">{letter.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(letter.createdAt, { includeTime: true })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <PermissionGate permission="exit_management" action="read">
                              <button onClick={() => handleViewLetter(letter)} className="text-[#0F2942] hover:text-blue-900" title="View Details">
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="exit_management" action="read">
                              <button onClick={() => handleDownloadLetter(letter)} className="text-purple-600 hover:text-purple-900" title="Download Letter">
                                <Download className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="exit_management" action="update">
                              <button onClick={() => handleEditLetter(letter)} className="text-indigo-600 hover:text-indigo-900" title="Edit Letter">
                                <Edit className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="exit_management" action="update">
                              <button onClick={() => handleSendLetterClick(letter)} className="text-green-600 hover:text-green-900" title="Send Letter">
                                <Send className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {canDeleteLetter && (
                              <PermissionGate permission="exit_management" action="delete">
                                <button onClick={() => handleDeleteLetter(letter)} className="text-red-600 hover:text-red-900" title="Delete">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationControls
                currentPage={lettersPage}
                totalPages={lettersTotalPages}
                totalItems={lettersTotalItems}
                limit={lettersPageSize}
                hasNextPage={lettersPage < lettersTotalPages}
                hasPrevPage={lettersPage > 1}
                onPageChange={(p) => setLettersPage(p)}
                onLimitChange={(l) => { setLettersPageSize(l); }}
                limitOptions={[10, 20, 50]}
                border={false}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relieving Letter Management</h1>
        </div>
        <div className="flex items-center space-x-4">
          <PermissionGate permission="exit_management" action="delete">
            {selectedRows.size > 0 && (
              <Button
                variant="danger"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white"
              >
                {isBulkDeleting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span>Delete Selected ({selectedRows.size})</span>
              </Button>
            )}
          </PermissionGate>

          <PermissionGate permission="exit_management" action="read">
            <Button
              variant="outline"
              onClick={selectedRows.size > 0 ? handleExportSelected : handleExport}
              className="flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>{selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}</span>
            </Button>
          </PermissionGate>
          <PermissionGate permission="exit_management" action="create">
            <button
              onClick={() => {
                resetLetterForm();
                setShowLetterForm(true);
              }}
              className="btn-cancel flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Letter
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Content */}
      {showLetterForm ? renderLetterForm() : renderLettersList()}

      {/* Preview Modal - Letter Format */}
      {showPreview && previewLetter && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 !mt-0 flex items-center justify-center p-4">
          <div className="relative bg-white shadow-2xl rounded-lg w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center shrink-0">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Relieving Letter Preview</h3>
                <p className="text-sm text-gray-500">Edit the content below as needed before saving or downloading.</p>
              </div>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewLetter(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-200 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body - Scrollable Letter Area */}
            <div className="flex-1 overflow-y-auto bg-gray-100 p-4 md:p-8">
              <div className="bg-white mx-auto shadow-xl w-full min-h-[297mm] max-w-[210mm] p-[25mm] text-[#334155] font-sans leading-relaxed relative">

                {/* Letter Header: Logo Left, Company Name Center */}
                <div className="relative mb-12 flex items-center">
                  <div className="absolute left-0 top-0 w-20">
                    <img src="/logo.png" className="max-w-full" alt="Logo"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="flex-1 text-center">
                    <h1 className="text-2xl font-bold text-[#3B82F6]">{process.env.REACT_APP_COMPANY_NAME}</h1>
                    <h2 className="text-xl font-bold text-[#3B82F6] mt-4">RELIEVING LETTER</h2>
                  </div>
                </div>

                {/* Date - Right Aligned & Editable */}
                <div className="text-right mb-10 text-[15px]">
                  <div className="inline-flex flex-col items-end">
                    <div className="flex items-center">
                      <span className="shrink-0">Date:</span>
                      <input
                        type="text"
                        placeholder="e.g. 27 February, 2026"
                        className={`border-none p-0 focus:ring-0 rounded bg-transparent text-right w-56 ml-1 ${letterForm.issueDate && isNaN(new Date(letterForm.issueDate).getTime()) && !/[a-zA-Z]/.test(letterForm.issueDate)
                          ? 'text-red-500 bg-red-50'
                          : 'focus:bg-blue-50'
                          }`}
                        value={letterForm.issueDate || new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        onChange={(e) => setLetterForm({ ...letterForm, issueDate: e.target.value })}
                      />
                    </div>
                    {letterForm.issueDate && isNaN(new Date(letterForm.issueDate).getTime()) && !/[a-zA-Z]/.test(letterForm.issueDate) && (
                      <span className="text-[10px] text-red-500 mt-1">Invalid date format</span>
                    )}
                  </div>
                </div>

                {/* Salutation - Editable */}
                <div className="mb-6 text-[15px]">
                  <input
                    className="w-full border-none p-0 focus:ring-0 focus:bg-blue-50 rounded bg-transparent font-medium"
                    value={letterForm.letterContent.salutation}
                    onChange={(e) => setLetterForm({ ...letterForm, letterContent: { ...letterForm.letterContent, salutation: e.target.value } })}
                  />
                </div>

                {/* Body - Editable Textarea */}
                <div className="mb-12 text-[15px]">
                  <textarea
                    className="w-full border-none p-0 focus:ring-0 focus:bg-blue-50 rounded bg-transparent resize-none leading-[1.8] text-justify"
                    rows={14}
                    value={letterForm.letterContent.body}
                    onChange={(e) => setLetterForm({ ...letterForm, letterContent: { ...letterForm.letterContent, body: e.target.value } })}
                  />
                </div>

                {/* Signature Block - Left Aligned */}
                <div className="text-[15px] space-y-1">
                  <p className="mb-4">{process.env.REACT_APP_COMPANY_NAME},</p>
                  <p className="mb-8">(Authorized Signatory)</p>

                  <div className="pt-4 space-y-2">
                    <div className="flex items-center">
                      <span className="w-20">Name:</span>
                      <input
                        className="flex-1 font-bold border-none p-0 focus:ring-0 focus:bg-blue-50 rounded bg-transparent uppercase"
                        value={letterForm.letterContent.signature}
                        onChange={(e) => setLetterForm({ ...letterForm, letterContent: { ...letterForm.letterContent, signature: e.target.value } })}
                      />
                    </div>
                    <div className="flex items-center">
                      <span className="w-24">Designation:</span>
                      <input
                        className="flex-1 border-none p-0 focus:ring-0 focus:bg-blue-50 rounded bg-transparent"
                        value={letterForm.letterContent.designation}
                        onChange={(e) => setLetterForm({ ...letterForm, letterContent: { ...letterForm.letterContent, designation: e.target.value } })}
                      />
                    </div>
                    <div className="flex items-center">
                      <span className="w-24 shrink-0">Seal/Stamp:</span>
                      <div className="border-b border-gray-400 w-48 h-5 ml-1"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer - Actions */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3 shrink-0">
              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewLetter(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Back to Form
              </button>

              <button
                onClick={async () => {
                  const letterWithEdits = {
                    ...previewLetter,
                    letterContent: letterForm.letterContent // Use current form content which includes edits
                  };
                  await handleDownloadLetter(letterWithEdits);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 transition-colors flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </button>

              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewLetter(null);
                  handleSubmitLetter({ preventDefault: () => { } }); // Trigger form submission
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 transition-colors flex items-center"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Finalize & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {showSendModal && letterToSend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Send Relieving Letter</h3>
                <button
                  onClick={handleCloseSendModal}
                  disabled={isSending}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 break-words">
                Are you sure you want to send the relieving letter to <span className="font-semibold break-words">{letterToSend.employeeEmail || 'email address'}</span>?
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCloseSendModal}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSendLetter}
                disabled={isSending}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        title="Export Relieving Letters"
        loading={exportLoading}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && letterToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Delete Relieving Letter</h3>
                <button
                  onClick={handleCloseDeleteModal}
                  disabled={isDeleting}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 break-words">
                Are you sure you want to delete the relieving letter for <span className="font-semibold break-words">{letterToDelete.employeeName || 'this employee'}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelievingLetter;